import React, { useState, useEffect, useRef } from 'react';
import { 
  Play, 
  Pause, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  MousePointer, 
  BookOpen, 
  User, 
  Shield, 
  Crown, 
  DollarSign, 
  AlertCircle, 
  CheckCircle, 
  Calendar, 
  Clock, 
  Plus, 
  Send, 
  FileDown,
  FileText, 
  UserPlus, 
  Settings, 
  HelpCircle,
  MessageSquare,
  ArrowRight,
  Sparkles,
  Search,
  RotateCcw,
  LayoutDashboard,
  Upload,
  CheckSquare,
  ShieldCheck,
  Download,
  Calculator,
  BarChart2,
  Users,
  Fingerprint,
  Sliders,
  CreditCard,
  Building2,
  Award,
  Layers,
  Check,
  ExternalLink,
  Lock
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface InteractiveWalkthroughProps {
  onClose: () => void;
}

type Mode = 'autoplay' | 'manual';

interface WalkthroughStep {
  title: string;
  role: 'employee' | 'manager' | 'admin' | 'all';
  icon: React.ReactNode;
  description: string;
  takeaways: string[];
  mockView: string;
  sidebarTarget?: string;
}

export const InteractiveWalkthrough: React.FC<InteractiveWalkthroughProps> = ({ onClose }) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [mode, setMode] = useState<Mode>('autoplay');
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [subStep, setSubStep] = useState<number>(0);
  const [speedMultiplier, setSpeedMultiplier] = useState<number>(1); // 1x or 1.5x
  const [mobileTab, setMobileTab] = useState<'guide' | 'simulator'>('guide');
  
  // Settings Studio Interactive Sub-Tab & Section State
  const [settingsActiveSection, setSettingsActiveSection] = useState<
    'general_pos' | 'shifts_hours' | 'staff_roster' | 'tip_allocations' | 'business_profile' |
    'subscription_billing' | 'referral_program' | 'tipflow_guide' | 'contact_support'
  >('general_pos');
  const [settingsActiveSubTab, setSettingsActiveSubTab] = useState<'pos' | 'service_fees' | 'payroll' | 'irs_8027'>('pos');
  const [settingsGuideSubTab, setSettingsGuideSubTab] = useState<number>(1);
  const [settingsSelectedPos, setSettingsSelectedPos] = useState<string>('toast');
  const [settingsSelectedPayroll, setSettingsSelectedPayroll] = useState<string>('gusto');
  const [settingsCcOffsetEnabled, setSettingsCcOffsetEnabled] = useState<boolean>(true);
  const [settingsStateJurisdiction, setSettingsStateJurisdiction] = useState<string>('CA');
  const [settingsAllocationRule, setSettingsAllocationRule] = useState<string>('hours_weighted');

  // Simulated form states
  const [simulatedCashAmount, setSimulatedCashAmount] = useState<string>('');
  const [simulatedJarAmount, setSimulatedJarAmount] = useState<string>('');
  const [simulatedDisputeReason, setSimulatedDisputeReason] = useState<string>('');
  const [simulatedStaffName, setSimulatedStaffName] = useState<string>('');
  const [simulatedStaffPosId, setSimulatedStaffPosId] = useState<string>('');
  const [simulatedInviteEmail, setSimulatedInviteEmail] = useState<string>('');
  const [simulatedSignatureDone, setSimulatedSignatureDone] = useState<boolean>(false);
  const [simulatedPosBatchUploaded, setSimulatedPosBatchUploaded] = useState<boolean>(false);
  
  // Active simulation animation states
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [isSidebarHovered, setIsSidebarHovered] = useState<string | null>(null);
  const [cursorPos, setCursorPos] = useState<{ x: string; y: string; opacity: number; clickEffect: boolean }>({
    x: '50%',
    y: '50%',
    opacity: 0,
    clickEffect: false
  });
  
  const moveCursorTo = (elementId: string, clickEffect: boolean = false) => {
    const container = document.getElementById('simulator-section');
    const el = document.getElementById(elementId);
    if (el && container) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const x = ((elRect.left + elRect.width / 2 - containerRect.left) / containerRect.width) * 100;
      const y = ((elRect.top + elRect.height / 2 - containerRect.top) / containerRect.height) * 100;
      setCursorPos({ x: `${x}%`, y: `${y}%`, opacity: 1, clickEffect });
    }
  };
  
  // Timer refs for managing Auto-Play intervals
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const cursorTimerRef = useRef<NodeJS.Timeout | null>(null);

  const steps: WalkthroughStep[] = [
    {
      title: 'Welcome to TipValue AI',
      role: 'all',
      icon: <Sparkles size={20} className="text-blue-500 animate-pulse" />,
      description: 'Experience how TipValue streamlines daily tip logging, automated POS imports, employee e-signatures, dispute resolution, and one-click payroll exports.',
      takeaways: [
        'Complete transparency and digital shift sign-offs for employees.',
        'Automated POS imports & smart allocation rules for managers.',
        'Audit-ready Gusto, ADP, and Paychex payroll exports for admins.'
      ],
      mockView: 'welcome'
    },
    {
      title: 'Employee: "My Tips" Dashboard',
      role: 'employee',
      icon: <LayoutDashboard size={20} className="text-emerald-500" />,
      description: 'Employees log in to inspect their shift breakdown, credit card POS tips, cash collections, and certified approval statuses in real time.',
      takeaways: [
        'Live summary cards for POS card tips, cash, and pooled jars.',
        'Chronological shift logs showing exact hours and point weights.',
        'Instant status indicators: Approved, In Review, or Disputed.'
      ],
      mockView: 'employee_dashboard',
      sidebarTarget: 'Operations Dashboard'
    },
    {
      title: 'Employee: Submit Cash & Jar Tips',
      role: 'employee',
      icon: <DollarSign size={20} className="text-emerald-500" />,
      description: 'Roster members log their daily physical cash collections and shared jar counts under "Submit Cash Tips" with full timestamp audit trails.',
      takeaways: [
        'Pre-linked employee profile prevents misallocated pools.',
        'Shift block tagging ensures tips reach the right team members.',
        'Instant history tab to audit personal cash submission records.'
      ],
      mockView: 'submit_cash',
      sidebarTarget: 'Submit Cash Tips'
    },
    {
      title: 'Employee: Digital Sign-off & Approvals',
      role: 'employee',
      icon: <Fingerprint size={20} className="text-blue-500" />,
      description: 'Employees review their official pay period tip ledger and sign off digitally, establishing strict IRS Form 8027 compliance and eliminating disputes.',
      takeaways: [
        'Inspect certified shift allocations grouped by official pay periods.',
        'Digital signature pad for employee shift verification.',
        'Maintains immutable compliance records for tax and wage auditing.'
      ],
      mockView: 'employee_approvals',
      sidebarTarget: 'Approval & Sign-off'
    },
    {
      title: 'Employee: Filing a Discrepancy Dispute',
      role: 'employee',
      icon: <AlertCircle size={20} className="text-rose-500" />,
      description: 'If an employee identifies a timesheet error or miscalculated shift, they flag the row under "Dispute Management" with notes for management review.',
      takeaways: [
        'Locks the shift to prevent premature payroll exports.',
        'Attaches detailed employee feedback on what needs correction.',
        'Live resolution tracking from filing to approved manager override.'
      ],
      mockView: 'file_dispute',
      sidebarTarget: 'Dispute Management'
    },
    {
      title: 'Manager: Import POS Tips',
      role: 'manager',
      icon: <Upload size={20} className="text-indigo-500" />,
      description: 'Managers upload POS export files (Toast, Square, Clover, Aloha) to automatically parse server sales, card tips, and employee register IDs.',
      takeaways: [
        'Drag-and-drop CSV / Excel batch upload with column auto-mapping.',
        'Instant preview table showing transaction counts and tip totals.',
        'Automatic register-to-roster ID matching prevents unassigned tips.'
      ],
      mockView: 'pos_import',
      sidebarTarget: 'Import POS Tips'
    },
    {
      title: 'Manager: Review & Approve Tips',
      role: 'manager',
      icon: <CheckSquare size={20} className="text-emerald-500" />,
      description: 'Managers quickly verify raw submitted cash and imported POS records on the Review queue before locking them into the shift allocation pool.',
      takeaways: [
        'Unified stream of all pending POS, Cash, and Jar records.',
        'Clear source badges indicating submission origin.',
        'Single-click batch approval of all verified shift items.'
      ],
      mockView: 'review_approve',
      sidebarTarget: 'Review & Approve Tips'
    },
    {
      title: 'Manager: Review Allocations & Disputes',
      role: 'manager',
      icon: <ShieldCheck size={20} className="text-blue-500" />,
      description: 'Oversee pooled shift calculations, role point weightings, and resolve flagged employee disputes side-by-side with manager override controls.',
      takeaways: [
        'Hours-weighted and points-based pool distributions.',
        'Side-by-side dispute investigation with punch log verification.',
        'Direct override adjustments with auditable manager notes.'
      ],
      mockView: 'manager_review',
      sidebarTarget: 'Review Allocations'
    },
    {
      title: 'Payroll: Run Tip Payroll Export',
      role: 'manager',
      icon: <Download size={20} className="text-blue-500" />,
      description: 'Generate ADP, Gusto, or Paychex compatible spreadsheet exports in seconds, fully calculated with hours-weights and state compliance adjustments.',
      takeaways: [
        'Select official pay period date ranges with automated scoping.',
        'Calculates consolidated credit card, cash, and jar totals per employee.',
        'One-click download of payroll CSVs ready for payroll processors.'
      ],
      mockView: 'run_payroll',
      sidebarTarget: 'Run Tip Payroll Export'
    },
    {
      title: 'Admin: Staff Roster & Magic Invites',
      role: 'admin',
      icon: <Users size={20} className="text-purple-500" />,
      description: 'Manage employee profiles, map register POS IDs, set custom role point weights, and dispatch secure token invite links to new team members.',
      takeaways: [
        'Map employee profiles to physical POS register numbers.',
        'Configure role point weights (e.g. Bartender 1.2x, Server 1.0x).',
        'Generate secure token links for passwordless onboarding.'
      ],
      mockView: 'user_management',
      sidebarTarget: 'Users'
    },
    {
      title: 'Admin: Operations & Settings Studio',
      role: 'admin',
      icon: <Settings size={20} className="text-purple-500" />,
      description: 'Fine-tune your entire tipping engine: POS integrations, mandatory service fee splits, credit card fee offsets, payroll cycles, and IRS Form 8027 rules.',
      takeaways: [
        'POS Settings: Toast, Square, Clover sync & shift mappings.',
        'Service Fees & CC Offsets: Auto-grat splits & state deduction rules.',
        'Payroll: Provider presets (Gusto, ADP) and pay period anchors.',
        'IRS Form 8027: 8% Gross Receipts shortfall threshold engine.'
      ],
      mockView: 'settings_studio',
      sidebarTarget: 'Settings'
    },
    {
      title: 'Interactive Tour Completed!',
      role: 'all',
      icon: <Sparkles size={20} className="text-yellow-500 animate-bounce" />,
      description: 'You are ready! You now know how TipValue coordinates daily shift operations, employee verification, deep settings configuration, and payroll exports.',
      takeaways: [
        'Empower employees with complete tip transparency and e-signatures.',
        'Eliminate manual calculation spreadsheets for managers.',
        'Maintain 100% audit-ready compliance across all operations.'
      ],
      mockView: 'completed'
    }
  ];

  const currentStepData = steps[activeStep];

  // Auto-play state machine simulation speed
  const stepDelay = 8500 / speedMultiplier;
  const subStepDelay = 1500 / speedMultiplier;

  // Restart the current sub-step simulation when entering a step
  useEffect(() => {
    setSubStep(0);
    setAlertMessage(null);
    setIsSidebarHovered(null);
    setSimulatedCashAmount('');
    setSimulatedJarAmount('');
    setSimulatedDisputeReason('');
    setSimulatedStaffName('');
    setSimulatedStaffPosId('');
    setSimulatedInviteEmail('');
    setSimulatedSignatureDone(false);
    setSimulatedPosBatchUploaded(false);
    
    if (autoPlayTimerRef.current) clearTimeout(autoPlayTimerRef.current);
    if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);

    // Initial cursor placement
    setCursorPos({ x: '50%', y: '50%', opacity: 0, clickEffect: false });
  }, [activeStep]);

  // Handle active step sub-simulation sequences (like a video)
  useEffect(() => {
    if (mode !== 'autoplay' || !isPlaying) {
      if (autoPlayTimerRef.current) clearTimeout(autoPlayTimerRef.current);
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
      setCursorPos(prev => ({ ...prev, opacity: 0 }));
      return;
    }

    const runSimulation = () => {
      const view = currentStepData.mockView;

      if (view === 'welcome') {
        setCursorPos({ x: '55%', y: '65%', opacity: 1, clickEffect: false });
        
        cursorTimerRef.current = setTimeout(() => {
          setCursorPos(prev => ({ ...prev, clickEffect: true }));
          setTimeout(() => {
            setCursorPos(prev => ({ ...prev, clickEffect: false }));
          }, 300);
          
          autoPlayTimerRef.current = setTimeout(() => {
            handleNext();
          }, 3500);
        }, 1500);
      } 
      
      else if (view === 'employee_dashboard') {
        moveCursorTo('sim-dashboard-pos-card');
        
        cursorTimerRef.current = setTimeout(() => {
          moveCursorTo('sim-dashboard-jar-card');
          
          cursorTimerRef.current = setTimeout(() => {
            moveCursorTo('sim-dashboard-shift-log');
            
            autoPlayTimerRef.current = setTimeout(() => {
              handleNext();
            }, 3500);
          }, 2000);
        }, 2000);
      } 
      
      else if (view === 'submit_cash') {
        setIsSidebarHovered('Submit Cash Tips');
        moveCursorTo('sim-sidebar-link-submit-cash-tips');
        
        cursorTimerRef.current = setTimeout(() => {
          setCursorPos(prev => ({ ...prev, clickEffect: true }));
          setIsSidebarHovered(null);
          
          setTimeout(() => {
            moveCursorTo('submit-cash-amount-input');
            
            cursorTimerRef.current = setTimeout(() => {
              let amountText = '150.00';
              let currentText = '';
              const typingInterval = setInterval(() => {
                if (currentText.length < amountText.length) {
                  currentText += amountText[currentText.length];
                  setSimulatedCashAmount(currentText);
                } else {
                  clearInterval(typingInterval);
                  
                  moveCursorTo('submit-cash-submit-button');
                  
                  cursorTimerRef.current = setTimeout(() => {
                    setCursorPos(prev => ({ ...prev, clickEffect: true }));
                    setAlertMessage('Cash Tip submitted successfully! $150.00 logged in Pending Status.');
                    
                    setTimeout(() => {
                      setCursorPos(prev => ({ ...prev, clickEffect: false }));
                      
                      moveCursorTo('submit-cash-history-tab');
                      
                      cursorTimerRef.current = setTimeout(() => {
                        setCursorPos(prev => ({ ...prev, clickEffect: true }));
                        setSubStep(1);
                        
                        setTimeout(() => {
                          setCursorPos(prev => ({ ...prev, clickEffect: false }));
                          
                          autoPlayTimerRef.current = setTimeout(() => {
                            handleNext();
                          }, 3000);
                        }, 300);
                      }, 1500);
                    }, 300);
                  }, 1500);
                }
              }, 150);
            }, 1000);
          }, 300);
        }, 1500);
      } 

      else if (view === 'employee_approvals') {
        setIsSidebarHovered('Approval & Sign-off');
        moveCursorTo('sim-sidebar-link-approval-&-sign-off');

        cursorTimerRef.current = setTimeout(() => {
          setCursorPos(prev => ({ ...prev, clickEffect: true }));
          setIsSidebarHovered(null);

          setTimeout(() => {
            moveCursorTo('sim-approvals-period-card');

            cursorTimerRef.current = setTimeout(() => {
              moveCursorTo('sim-approvals-sign-button');

              cursorTimerRef.current = setTimeout(() => {
                setCursorPos(prev => ({ ...prev, clickEffect: true }));
                setSubStep(1); // Show signature pad

                setTimeout(() => {
                  setCursorPos(prev => ({ ...prev, clickEffect: false }));
                  moveCursorTo('sim-approvals-confirm-sign');

                  cursorTimerRef.current = setTimeout(() => {
                    setCursorPos(prev => ({ ...prev, clickEffect: true }));
                    setSimulatedSignatureDone(true);
                    setAlertMessage('Pay Period tip records signed and verified for IRS compliance!');

                    setTimeout(() => {
                      setCursorPos(prev => ({ ...prev, clickEffect: false }));
                      setSubStep(2); // Show signed status badge

                      autoPlayTimerRef.current = setTimeout(() => {
                        handleNext();
                      }, 3200);
                    }, 300);
                  }, 1500);
                }, 400);
              }, 1500);
            }, 1500);
          }, 300);
        }, 1500);
      }
      
      else if (view === 'file_dispute') {
        setIsSidebarHovered('Dispute Management');
        moveCursorTo('sim-sidebar-link-dispute-management');

        cursorTimerRef.current = setTimeout(() => {
          setCursorPos(prev => ({ ...prev, clickEffect: true }));
          setIsSidebarHovered(null);

          setTimeout(() => {
            moveCursorTo('sim-file-dispute-trigger');
            
            cursorTimerRef.current = setTimeout(() => {
              setCursorPos(prev => ({ ...prev, clickEffect: true }));
              setSubStep(1);
              
              setTimeout(() => {
                setCursorPos(prev => ({ ...prev, clickEffect: false }));
                moveCursorTo('sim-dispute-reason-input');
                
                cursorTimerRef.current = setTimeout(() => {
                  setCursorPos(prev => ({ ...prev, clickEffect: true }));
                  setSimulatedDisputeReason('Hours incorrect on night shift (worked 8 hrs instead of 5 hrs)');
                  
                  setTimeout(() => {
                    setCursorPos(prev => ({ ...prev, clickEffect: false }));
                    moveCursorTo('sim-submit-dispute-button');
                    
                    cursorTimerRef.current = setTimeout(() => {
                      setCursorPos(prev => ({ ...prev, clickEffect: true }));
                      setAlertMessage('Dispute submitted successfully! Shift allocation locked for manager review.');
                      
                      setTimeout(() => {
                        setCursorPos(prev => ({ ...prev, clickEffect: false }));
                        setSubStep(2);
                        
                        autoPlayTimerRef.current = setTimeout(() => {
                          handleNext();
                        }, 3500);
                      }, 300);
                    }, 1500);
                  }, 300);
                }, 1500);
              }, 300);
            }, 1500);
          }, 300);
        }, 1500);
      }

      else if (view === 'pos_import') {
        setIsSidebarHovered('Import POS Tips');
        moveCursorTo('sim-sidebar-link-import-pos-tips');

        cursorTimerRef.current = setTimeout(() => {
          setCursorPos(prev => ({ ...prev, clickEffect: true }));
          setIsSidebarHovered(null);

          setTimeout(() => {
            moveCursorTo('sim-pos-dropzone');

            cursorTimerRef.current = setTimeout(() => {
              setCursorPos(prev => ({ ...prev, clickEffect: true }));
              setSubStep(1); // File uploaded & parsed
              setSimulatedPosBatchUploaded(true);
              setAlertMessage('Toast_Shift_Report_July11.csv uploaded! 42 tip records parsed.');

              setTimeout(() => {
                setCursorPos(prev => ({ ...prev, clickEffect: false }));
                moveCursorTo('sim-pos-process-button');

                cursorTimerRef.current = setTimeout(() => {
                  setCursorPos(prev => ({ ...prev, clickEffect: true }));
                  setSubStep(2); // Processed
                  setAlertMessage('Batch processed! $1,480.00 POS credit tips assigned to roster members.');

                  setTimeout(() => {
                    setCursorPos(prev => ({ ...prev, clickEffect: false }));

                    autoPlayTimerRef.current = setTimeout(() => {
                      handleNext();
                    }, 3500);
                  }, 300);
                }, 1800);
              }, 400);
            }, 1500);
          }, 300);
        }, 1500);
      }

      else if (view === 'review_approve') {
        setIsSidebarHovered('Review & Approve Tips');
        moveCursorTo('sim-sidebar-link-review-&-approve-tips');

        cursorTimerRef.current = setTimeout(() => {
          setCursorPos(prev => ({ ...prev, clickEffect: true }));
          setIsSidebarHovered(null);

          setTimeout(() => {
            moveCursorTo('sim-review-approve-all-btn');

            cursorTimerRef.current = setTimeout(() => {
              setCursorPos(prev => ({ ...prev, clickEffect: true }));
              setSubStep(1); // All approved
              setAlertMessage('All 6 pending tips pre-approved and ready for pool allocation!');

              setTimeout(() => {
                setCursorPos(prev => ({ ...prev, clickEffect: false }));

                autoPlayTimerRef.current = setTimeout(() => {
                  handleNext();
                }, 3500);
              }, 300);
            }, 1800);
          }, 300);
        }, 1500);
      }
      
      else if (view === 'manager_review') {
        setIsSidebarHovered('Review Allocations');
        moveCursorTo('sim-sidebar-link-review-allocations');

        cursorTimerRef.current = setTimeout(() => {
          setCursorPos(prev => ({ ...prev, clickEffect: true }));
          setIsSidebarHovered(null);

          setTimeout(() => {
            moveCursorTo('sim-dispute-resolve-btn');
            
            cursorTimerRef.current = setTimeout(() => {
              setCursorPos(prev => ({ ...prev, clickEffect: true }));
              setSubStep(1); // Open Resolution Drawer
              
              setTimeout(() => {
                setCursorPos(prev => ({ ...prev, clickEffect: false }));
                moveCursorTo('sim-dispute-approve-override-btn');
                
                cursorTimerRef.current = setTimeout(() => {
                  setCursorPos(prev => ({ ...prev, clickEffect: true }));
                  setAlertMessage('Dispute resolved! Hours corrected & tip adjusted to $195.00.');
                  
                  setTimeout(() => {
                    setCursorPos(prev => ({ ...prev, clickEffect: false }));
                    setSubStep(2); // Resolved state
                    
                    autoPlayTimerRef.current = setTimeout(() => {
                      handleNext();
                    }, 3500);
                  }, 300);
                }, 1800);
              }, 300);
            }, 1500);
          }, 300);
        }, 1500);
      } 
      
      else if (view === 'run_payroll') {
        setIsSidebarHovered('Run Tip Payroll Export');
        moveCursorTo('sim-sidebar-link-run-tip-payroll-export');
        
        cursorTimerRef.current = setTimeout(() => {
          setCursorPos(prev => ({ ...prev, clickEffect: true }));
          setIsSidebarHovered(null);
          
          setTimeout(() => {
            moveCursorTo('sim-payroll-export-button');
            
            cursorTimerRef.current = setTimeout(() => {
              setCursorPos(prev => ({ ...prev, clickEffect: true }));
              setSubStep(1); // Loader
              
              setTimeout(() => {
                setCursorPos(prev => ({ ...prev, clickEffect: false }));
                
                cursorTimerRef.current = setTimeout(() => {
                  setSubStep(2); // CSV card
                  setAlertMessage('Payroll cycle calculated! Consolidated tip records ready for download.');
                  
                  moveCursorTo('sim-payroll-download-button');
                  
                  cursorTimerRef.current = setTimeout(() => {
                    setCursorPos(prev => ({ ...prev, clickEffect: true }));
                    
                    setTimeout(() => {
                      setCursorPos(prev => ({ ...prev, clickEffect: false }));
                      setSubStep(3);
                      
                      autoPlayTimerRef.current = setTimeout(() => {
                        handleNext();
                      }, 3500);
                    }, 300);
                  }, 1800);
                }, 1800);
              }, 300);
            }, 1500);
          }, 300);
        }, 1500);
      } 
      
      else if (view === 'user_management') {
        setIsSidebarHovered('Users');
        moveCursorTo('sim-sidebar-link-users');

        cursorTimerRef.current = setTimeout(() => {
          setCursorPos(prev => ({ ...prev, clickEffect: true }));
          setIsSidebarHovered(null);

          setTimeout(() => {
            moveCursorTo('sim-add-staff-button');
            
            cursorTimerRef.current = setTimeout(() => {
              setCursorPos(prev => ({ ...prev, clickEffect: true }));
              setSubStep(1); // Open drawer
              
              setTimeout(() => {
                setCursorPos(prev => ({ ...prev, clickEffect: false }));
                moveCursorTo('staff-name-input');
                
                cursorTimerRef.current = setTimeout(() => {
                  let nameVal = 'John Doe';
                  let cur = '';
                  const typ = setInterval(() => {
                    if (cur.length < nameVal.length) {
                      cur += nameVal[cur.length];
                      setSimulatedStaffName(cur);
                    } else {
                      clearInterval(typ);
                      
                      moveCursorTo('staff-pos-id-input');
                      
                      cursorTimerRef.current = setTimeout(() => {
                        let posVal = 'POS-998';
                        let curP = '';
                        const typP = setInterval(() => {
                          if (curP.length < posVal.length) {
                            curP += posVal[curP.length];
                            setSimulatedStaffPosId(curP);
                          } else {
                            clearInterval(typP);
                            
                            moveCursorTo('staff-save-button');
                            
                            cursorTimerRef.current = setTimeout(() => {
                              setCursorPos(prev => ({ ...prev, clickEffect: true }));
                              setAlertMessage('Roster Member John Doe added & linked to POS ID POS-998!');
                              
                              setTimeout(() => {
                                setCursorPos(prev => ({ ...prev, clickEffect: false }));
                                setSubStep(2); // Updated list
                                
                                autoPlayTimerRef.current = setTimeout(() => {
                                  handleNext();
                                }, 3500);
                              }, 300);
                            }, 1500);
                          }
                        }, 100);
                      }, 1000);
                    }
                  }, 100);
                }, 1000);
              }, 300);
            }, 1500);
          }, 300);
        }, 1500);
      } 
      
      else if (view === 'settings_studio') {
        setIsSidebarHovered('Settings');
        moveCursorTo('sim-sidebar-link-settings');

        // Start by ensuring we are in general_pos section
        setSettingsActiveSection('general_pos');
        setSettingsActiveSubTab('pos');

        cursorTimerRef.current = setTimeout(() => {
          setCursorPos(prev => ({ ...prev, clickEffect: true }));
          setIsSidebarHovered(null);

          setTimeout(() => {
            setCursorPos(prev => ({ ...prev, clickEffect: false }));

            // --- SUB-TAB 1: POS Settings ---
            moveCursorTo('sim-settings-tab-pos');
            cursorTimerRef.current = setTimeout(() => {
              setCursorPos(prev => ({ ...prev, clickEffect: true }));
              setSettingsActiveSubTab('pos');
              setAlertMessage('1.1 POS Settings: Toast, Square, and Clover automated sync at 4:00 AM shift reset.');

              setTimeout(() => {
                setCursorPos(prev => ({ ...prev, clickEffect: false }));

                // --- SUB-TAB 2: Service Fees & CC Offsets ---
                moveCursorTo('sim-settings-tab-service-fees');
                cursorTimerRef.current = setTimeout(() => {
                  setCursorPos(prev => ({ ...prev, clickEffect: true }));
                  setSettingsActiveSubTab('service_fees');
                  setAlertMessage('1.2 Service Fees & CC Offsets: 80% FOH / 20% BOH split & state fee deduction locks.');

                  setTimeout(() => {
                    setCursorPos(prev => ({ ...prev, clickEffect: false }));

                    // --- SUB-TAB 3: Payroll Settings ---
                    moveCursorTo('sim-settings-tab-payroll');
                    cursorTimerRef.current = setTimeout(() => {
                      setCursorPos(prev => ({ ...prev, clickEffect: true }));
                      setSettingsActiveSubTab('payroll');
                      setAlertMessage('1.3 Payroll Settings: Gusto, ADP, Paychex presets and bi-weekly schedules.');

                      setTimeout(() => {
                        setCursorPos(prev => ({ ...prev, clickEffect: false }));

                        // --- SUB-TAB 4: IRS Form 8027 ---
                        moveCursorTo('sim-settings-tab-irs');
                        cursorTimerRef.current = setTimeout(() => {
                          setCursorPos(prev => ({ ...prev, clickEffect: true }));
                          setSettingsActiveSubTab('irs_8027');
                          setAlertMessage('1.4 IRS Form 8027: 8% gross food & beverage receipt shortfall tracking.');

                          setTimeout(() => {
                            setCursorPos(prev => ({ ...prev, clickEffect: false }));

                            // --- SECTION 2: Shifts & Hours ---
                            moveCursorTo('sim-settings-sec-shifts-hours');
                            cursorTimerRef.current = setTimeout(() => {
                              setCursorPos(prev => ({ ...prev, clickEffect: true }));
                              setSettingsActiveSection('shifts_hours');
                              setAlertMessage('2. Shifts & Hours: Lunch, Dinner, and Late Night shift operating blocks.');

                              setTimeout(() => {
                                setCursorPos(prev => ({ ...prev, clickEffect: false }));

                                // --- SECTION 3: Staff & Roster ---
                                moveCursorTo('sim-settings-sec-staff-roster');
                                cursorTimerRef.current = setTimeout(() => {
                                  setCursorPos(prev => ({ ...prev, clickEffect: true }));
                                  setSettingsActiveSection('staff_roster');
                                  setAlertMessage('3. Staff & Roster: Role point weights (Bartender 1.2x, Servers 1.0x, Bussers 0.6x).');

                                  setTimeout(() => {
                                    setCursorPos(prev => ({ ...prev, clickEffect: false }));

                                    // --- SECTION 4: Tip Allocations & Rules ---
                                    moveCursorTo('sim-settings-sec-tip-allocations');
                                    cursorTimerRef.current = setTimeout(() => {
                                      setCursorPos(prev => ({ ...prev, clickEffect: true }));
                                      setSettingsActiveSection('tip_allocations');
                                      setAlertMessage('4. Tip Allocations: Hours-weighted vs. point-weighted pool calculation engine.');

                                      setTimeout(() => {
                                        setCursorPos(prev => ({ ...prev, clickEffect: false }));

                                        // --- SECTION 5: Business Profile ---
                                        moveCursorTo('sim-settings-sec-business-profile');
                                        cursorTimerRef.current = setTimeout(() => {
                                          setCursorPos(prev => ({ ...prev, clickEffect: true }));
                                          setSettingsActiveSection('business_profile');
                                          setAlertMessage('5. Business Profile: Entity DBA, Federal EIN, and state jurisdiction.');

                                          setTimeout(() => {
                                            setCursorPos(prev => ({ ...prev, clickEffect: false }));

                                            // --- SECTION 6: Subscription & Billing ---
                                            moveCursorTo('sim-settings-sec-billing');
                                            cursorTimerRef.current = setTimeout(() => {
                                              setCursorPos(prev => ({ ...prev, clickEffect: true }));
                                              setSettingsActiveSection('subscription_billing');
                                              setAlertMessage('6. Billing & Subscription: Growth Pro $49/mo & active 30-day trial status.');

                                              setTimeout(() => {
                                                setCursorPos(prev => ({ ...prev, clickEffect: false }));

                                                // --- SECTION 7: Referral Program ---
                                                moveCursorTo('sim-settings-sec-referral');
                                                cursorTimerRef.current = setTimeout(() => {
                                                  setCursorPos(prev => ({ ...prev, clickEffect: true }));
                                                  setSettingsActiveSection('referral_program');
                                                  setAlertMessage('7. Referral Program: VIP affiliate code (TIPVALUE-VIP2026) & 20% commission.');

                                                  setTimeout(() => {
                                                    setCursorPos(prev => ({ ...prev, clickEffect: false }));

                                                    // --- SECTION 8: TipFlow Guide (5 Tabs) ---
                                                    moveCursorTo('sim-settings-sec-guide');
                                                    cursorTimerRef.current = setTimeout(() => {
                                                      setCursorPos(prev => ({ ...prev, clickEffect: true }));
                                                      setSettingsActiveSection('tipflow_guide');
                                                      setAlertMessage('8. TipFlow Documentation Hub: 5 dedicated guide modules.');

                                                      setTimeout(() => {
                                                        setCursorPos(prev => ({ ...prev, clickEffect: false }));

                                                        // --- SECTION 9: Contact Support ---
                                                        moveCursorTo('sim-settings-sec-support');
                                                        cursorTimerRef.current = setTimeout(() => {
                                                          setCursorPos(prev => ({ ...prev, clickEffect: true }));
                                                          setSettingsActiveSection('contact_support');
                                                          setAlertMessage('9. Contact Support: Priority ticket assistance with <15 min SLA.');

                                                          setTimeout(() => {
                                                            setCursorPos(prev => ({ ...prev, clickEffect: false }));

                                                            autoPlayTimerRef.current = setTimeout(() => {
                                                              handleNext();
                                                            }, 3500);
                                                          }, 300);
                                                        }, 1800);
                                                      }, 400);
                                                    }, 1800);
                                                  }, 400);
                                                }, 1800);
                                              }, 400);
                                            }, 1800);
                                          }, 400);
                                        }, 1800);
                                      }, 400);
                                    }, 1800);
                                  }, 400);
                                }, 1800);
                              }, 400);
                            }, 1800);
                          }, 400);
                        }, 1800);
                      }, 400);
                    }, 1800);
                  }, 400);
                }, 1800);
              }, 400);
            }, 1800);
          }, 300);
        }, 1200);
      } 
      
      else if (view === 'completed') {
        setCursorPos({ x: '50%', y: '75%', opacity: 1, clickEffect: false });
        
        autoPlayTimerRef.current = setTimeout(() => {
          setActiveStep(0);
        }, 12000);
      }
    };

    runSimulation();

    return () => {
      if (autoPlayTimerRef.current) clearTimeout(autoPlayTimerRef.current);
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
    };
  }, [activeStep, mode, isPlaying, speedMultiplier]);

  const handleNext = () => {
    setActiveStep((prev) => {
      if (prev < steps.length - 1) return prev + 1;
      return 0;
    });
  };

  const handleBack = () => {
    setActiveStep((prev) => (prev > 0 ? prev - 1 : steps.length - 1));
  };

  const selectStepDirectly = (index: number) => {
    setMode('manual');
    setIsPlaying(false);
    setActiveStep(index);
  };

  // Modernized Categorized Sidebar Items (matching Sidebar.tsx)
  const sidebarNavCategories = [
    {
      category: 'Daily Operations',
      items: [
        { label: 'Operations Dashboard', icon: <LayoutDashboard size={13} /> },
        { label: 'Submit Cash Tips', icon: <DollarSign size={13} /> },
        { label: 'Import POS Tips', icon: <Upload size={13} /> },
        { label: 'Review & Approve Tips', icon: <CheckSquare size={13} /> },
        { label: 'Review Allocations', icon: <ShieldCheck size={13} /> },
        { label: 'Dispute Management', icon: <AlertCircle size={13} /> },
      ]
    },
    {
      category: 'Payroll and Reporting',
      items: [
        { label: 'Run Tip Payroll Export', icon: <Download size={13} /> },
        { label: 'Reports', icon: <FileText size={13} /> },
        { label: 'Check IRS 8% Threshold', icon: <Calculator size={13} /> },
      ]
    },
    {
      category: 'Analytics & Team',
      items: [
        { label: 'Benchmark Dashboard', icon: <BarChart2 size={13} /> },
        { label: 'Chat', icon: <MessageSquare size={13} /> },
      ]
    },
    {
      category: 'Personal Account',
      items: [
        { label: 'My Tips', icon: <Users size={13} /> },
        { label: 'Approval & Sign-off', icon: <Fingerprint size={13} /> },
      ]
    },
    {
      category: 'Management',
      items: [
        { label: 'Users', icon: <Users size={13} /> },
        { label: 'Settings', icon: <Settings size={13} /> },
      ]
    }
  ];

  return (
    <div className="fixed inset-0 z-50 bg-gray-950/98 backdrop-blur-lg flex flex-col overflow-hidden text-white font-sans antialiased overscroll-none">
      
      {/* 1. TOAST NOTIFICATION OVERLAY (SIMULATED APP SHELL FEEDBACK) */}
      <AnimatePresence>
        {alertMessage && (
          <motion.div 
            initial={{ opacity: 0, y: -40, scale: 0.95 }}
            animate={{ opacity: 1, y: 16, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 18 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-full max-w-md bg-emerald-900/90 backdrop-blur border border-emerald-500/30 px-4 py-3 rounded-2xl shadow-xl flex items-center gap-3"
          >
            <CheckCircle className="text-emerald-400 shrink-0" size={20} />
            <span className="text-xs font-bold text-emerald-50 leading-snug">{alertMessage}</span>
            <button 
              onClick={() => setAlertMessage(null)}
              className="ml-auto text-emerald-400 hover:text-emerald-100 cursor-pointer p-1 rounded-lg"
            >
              <X size={14} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 2. TOP GLOBAL NAVIGATION & CONTROLLER */}
      <header className="px-4 md:px-6 py-3 border-b border-white/10 flex items-center justify-between bg-gray-900/60 shrink-0">
        
        {/* Left: Branding & Step Indicator */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <DollarSign size={18} className="text-white font-black" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-sm md:text-base tracking-tight text-white uppercase">TipValue AI</span>
              <span className="text-[10px] bg-blue-500/20 text-blue-400 font-bold px-2 py-0.5 rounded-full border border-blue-500/30">
                Interactive Guided Tour
              </span>
            </div>
            <p className="text-[11px] text-gray-400 hidden sm:block">
              Step {activeStep + 1} of {steps.length}: <strong className="text-gray-200">{currentStepData.title}</strong>
            </p>
          </div>
        </div>

        {/* Center: Mode Switching & Play/Pause Controls */}
        <div className="flex items-center gap-2 bg-gray-950/80 p-1 rounded-2xl border border-white/10">
          
          {/* Autoplay vs Manual Toggle */}
          <div className="flex items-center bg-gray-900 rounded-xl p-0.5 border border-white/5 text-xs">
            <button
              onClick={() => {
                setMode('autoplay');
                setIsPlaying(true);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                mode === 'autoplay' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Play size={12} className={isPlaying && mode === 'autoplay' ? 'animate-pulse' : ''} />
              <span className="hidden md:inline">Video Autoplay</span>
            </button>
            <button
              onClick={() => {
                setMode('manual');
                setIsPlaying(false);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                mode === 'manual' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <MousePointer size={12} />
              <span className="hidden md:inline">Interactive Step</span>
            </button>
          </div>

          {/* Autoplay specific toggles */}
          {mode === 'autoplay' && (
            <div className="flex items-center gap-1 pl-1 pr-2">
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="p-1.5 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg cursor-pointer transition-colors"
                title={isPlaying ? 'Pause Simulation' : 'Resume Simulation'}
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
              </button>
              <button
                onClick={() => setSpeedMultiplier(prev => (prev === 1 ? 1.5 : 1))}
                className="px-2 py-0.5 text-[10px] font-black rounded-md bg-white/5 border border-white/10 hover:bg-white/10 text-blue-400 cursor-pointer"
                title="Simulation Speed"
              >
                {speedMultiplier}x
              </button>
            </div>
          )}
        </div>

        {/* Right: Exit / Close Button */}
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/10 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer"
          >
            <span>Exit Tour</span>
            <X size={14} />
          </button>
        </div>
      </header>

      {/* MOBILE SCREEN SELECTOR TABS */}
      <div className="flex md:hidden border-b border-white/10 bg-gray-900">
        <button
          onClick={() => setMobileTab('guide')}
          className={`flex-1 py-2.5 text-xs font-extrabold text-center border-b-2 transition-all ${
            mobileTab === 'guide' ? 'text-blue-400 border-blue-500 bg-blue-500/10' : 'text-gray-400 border-transparent'
          }`}
        >
          1. Step Guide & Takeaways
        </button>
        <button
          onClick={() => setMobileTab('simulator')}
          className={`flex-1 py-2.5 text-xs font-extrabold text-center border-b-2 transition-all ${
            mobileTab === 'simulator' ? 'text-blue-400 border-blue-500 bg-blue-500/10' : 'text-gray-400 border-transparent'
          }`}
        >
          2. Live Interactive Simulator
        </button>
      </div>

      {/* 3. MAIN DUAL-PANE WALKTHROUGH WORKSPACE */}
      <main className="flex-1 flex flex-col md:flex-row overflow-hidden">
        
        {/* ======================================================== */}
        {/* LEFT COLUMN: GUIDED EXPLANATION & CONTROLS                */}
        {/* ======================================================== */}
        <section className={`w-full md:w-96 md:max-w-md bg-gray-900/40 border-r border-white/10 flex flex-col justify-between p-5 md:p-6 overflow-y-auto ${
          mobileTab === 'guide' ? 'flex' : 'hidden md:flex'
        }`}>
          
          <div className="space-y-6">
            
            {/* Active Step Badge & Role Tag */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-600 text-white font-black text-xs flex items-center justify-center shadow">
                  {activeStep + 1}
                </span>
                <span className="text-xs uppercase tracking-widest font-black text-gray-400">
                  Step {activeStep + 1} of {steps.length}
                </span>
              </div>
              <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                currentStepData.role === 'employee' 
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                  : currentStepData.role === 'manager'
                    ? 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                    : currentStepData.role === 'admin'
                      ? 'bg-purple-500/10 text-purple-400 border-purple-500/30'
                      : 'bg-amber-500/10 text-amber-400 border-amber-500/30'
              }`}>
                {currentStepData.role === 'all' ? 'All Roles' : `${currentStepData.role} view`}
              </span>
            </div>

            {/* Step Title and Icon */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-white/5 border border-white/10 shadow-inner">
                  {currentStepData.icon}
                </div>
                <h3 className="text-xl md:text-2xl font-black text-white tracking-tight leading-snug">
                  {currentStepData.title}
                </h3>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">
                {currentStepData.description}
              </p>
            </div>

            {/* Key Takeaways Box */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
              <h4 className="text-xs font-black uppercase tracking-wider text-blue-400 flex items-center gap-1.5">
                <Sparkles size={13} />
                <span>What Happens in This Step:</span>
              </h4>
              <ul className="space-y-2 text-xs text-gray-300">
                {currentStepData.takeaways.map((takeaway, idx) => (
                  <li key={idx} className="flex items-start gap-2 leading-relaxed">
                    <span className="text-blue-400 font-black mt-0.5">•</span>
                    <span>{takeaway}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pro Tip / Mode Helper */}
            <div className="p-3.5 rounded-xl bg-blue-950/40 border border-blue-500/20 text-xs text-blue-200/90 leading-relaxed flex items-start gap-2.5">
              <HelpCircle size={16} className="text-blue-400 shrink-0 mt-0.5" />
              <div>
                {mode === 'autoplay' ? (
                  <span>
                    The simulator on the right is auto-animating cursor actions, simulating inputs, and clicking triggers just like a video. Swap to <strong>Interactive Step</strong> at the top to take manual control.
                  </span>
                ) : (
                  <span>
                    <strong>Manual Exploration Active:</strong> You can click the buttons, tabs, and fields inside the simulator screen on the right.
                  </span>
                )}
              </div>
            </div>

          </div>

          {/* Bottom Step Navigation Bar */}
          <div className="pt-6 border-t border-white/10 space-y-4">
            
            {/* Step Selection Grid (Interactive Index dots) */}
            <div className="flex justify-between items-center gap-1 overflow-x-auto py-1">
              {steps.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => selectStepDirectly(idx)}
                  className={`h-2 rounded-full transition-all cursor-pointer ${
                    activeStep === idx 
                      ? 'w-6 bg-blue-500 shadow-sm shadow-blue-500/50' 
                      : idx < activeStep 
                        ? 'w-2 bg-blue-900 hover:bg-blue-700' 
                        : 'w-2 bg-gray-800 hover:bg-gray-700'
                  }`}
                  title={`${idx + 1}. ${s.title}`}
                />
              ))}
            </div>

            {/* Back / Next Buttons */}
            <div className="flex items-center justify-between gap-3">
              <button
                onClick={handleBack}
                className="flex-1 flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-gray-200 border border-white/10 py-2.5 rounded-xl text-xs font-black transition-all cursor-pointer"
              >
                <ChevronLeft size={16} />
                <span>Previous</span>
              </button>

              <button
                onClick={handleNext}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 py-2.5 rounded-xl text-xs font-black transition-all cursor-pointer"
              >
                <span>{activeStep === steps.length - 1 ? 'Finish Tour' : 'Next Step'}</span>
                <ChevronRight size={16} />
              </button>
            </div>

          </div>

        </section>

        {/* ======================================================== */}
        {/* RIGHT COLUMN: INTERACTIVE SIMULATOR FRAMEWORK            */}
        {/* ======================================================== */}
        <section 
          id="simulator-section" 
          className={`flex-1 bg-gray-950 flex flex-col overflow-hidden relative ${
            mobileTab === 'simulator' ? 'flex' : 'hidden md:flex'
          }`}
        >
          
          {/* Mobile Back to Guide Trigger */}
          <div className="flex md:hidden items-center justify-between bg-gray-900/90 px-4 py-2 border-b border-white/10 shrink-0">
            <button 
              onClick={() => setMobileTab('guide')}
              className="flex items-center gap-1.5 text-xs text-blue-400 font-bold hover:text-blue-300 transition-all cursor-pointer min-h-[30px]"
            >
              <ChevronLeft size={14} />
              <span>Back to Step Guide</span>
            </button>
            <div className="text-[9px] text-gray-500 uppercase tracking-widest font-black">Interactive Demo Screen</div>
          </div>

          {/* Simulated Mac OS Browser Header */}
          <div className="h-10 bg-gray-950 px-4 flex items-center justify-between border-b border-white/10 shrink-0 select-none">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-red-500/60 block"></span>
              <span className="w-3 h-3 rounded-full bg-yellow-500/60 block"></span>
              <span className="w-3 h-3 rounded-full bg-green-500/60 block"></span>
            </div>
            <div className="bg-gray-900 border border-white/5 px-4 py-1 rounded-md text-[10px] font-mono text-gray-400 w-1/2 text-center flex items-center justify-center gap-1 truncate">
              <span className="text-gray-600">https://</span>app.tipvalue.com{currentStepData.mockView === 'settings_studio' ? '/settings' : currentStepData.mockView === 'pos_import' ? '/pos-import' : currentStepData.mockView === 'employee_approvals' ? '/approvals' : currentStepData.mockView === 'run_payroll' ? '/payroll' : ''}
            </div>
            <div className="w-12 text-right">
              <span className="text-[9px] font-bold text-emerald-400 bg-emerald-950/60 px-1.5 py-0.5 rounded border border-emerald-500/20">LIVE</span>
            </div>
          </div>

          {/* Simulated App Space */}
          <div className="flex-1 flex overflow-hidden bg-gray-900 text-gray-800 text-xs relative select-none">
            
            {/* SIMULATED MODERN CATEGORIZED SIDEBAR */}
            <div className="w-14 md:w-48 shrink-0 bg-gray-950 border-r border-white/5 flex flex-col justify-between p-2 select-none overflow-y-auto">
              <div className="space-y-3">
                {/* Logo */}
                <div className="flex items-center gap-1.5 px-1 py-1 justify-center md:justify-start">
                  <div className="w-5 h-5 bg-blue-600 rounded-md flex items-center justify-center shrink-0">
                    <DollarSign size={11} className="text-white font-black" />
                  </div>
                  <span className="font-black text-[11px] text-white tracking-tight uppercase hidden md:inline-block">TipValue AI</span>
                </div>

                {/* Categorized Nav Links */}
                <div className="space-y-3">
                  {sidebarNavCategories.map((group, groupIdx) => (
                    <div key={groupIdx} className="space-y-0.5">
                      <div className="text-[8px] font-black uppercase tracking-wider text-gray-500 px-2 py-0.5 hidden md:block">
                        {group.category}
                      </div>
                      {group.items.map((link, idx) => {
                        const isHovered = isSidebarHovered === link.label;
                        const isTargeted = currentStepData.sidebarTarget === link.label;

                        return (
                          <div
                            key={idx}
                            id={`sim-sidebar-link-${link.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                            title={link.label}
                            className={`flex items-center justify-center md:justify-start gap-2 px-2 py-1 rounded-lg text-[9px] font-bold transition-all cursor-pointer ${
                              isTargeted 
                                ? 'bg-blue-600 text-white shadow-sm' 
                                : isHovered 
                                  ? 'bg-white/10 text-white' 
                                  : 'text-gray-400 hover:text-white hover:bg-white/5'
                            }`}
                          >
                            <span className="shrink-0">{link.icon}</span>
                            <span className="truncate hidden md:inline-block">{link.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Roster active status */}
              <div className="bg-white/5 p-1.5 rounded-xl border border-white/5 text-[8px] text-gray-400 flex flex-col gap-0.5 items-center md:items-start mt-2">
                <div className="flex items-center gap-1 font-extrabold text-blue-400">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full inline-block animate-pulse"></span>
                  <span className="hidden md:inline-block">CA JURISDICTION</span>
                </div>
                <span className="hidden md:inline-block truncate text-gray-300">San Francisco Venue #1</span>
              </div>
            </div>

            {/* SIMULATED WORKSPACE (DYNAMICS VIEWS BASED ON STEP) */}
            <div id="simulation-container" className="flex-1 bg-gray-50 flex flex-col overflow-hidden p-3 md:p-4 relative">
              
              {/* Simulated Header */}
              <div className="flex justify-between items-center border-b border-gray-200 pb-2 mb-3 shrink-0">
                <div>
                  <h4 className="font-extrabold text-[11px] md:text-[12px] text-gray-900 leading-tight">
                    {currentStepData.mockView === 'welcome' && 'Welcome Onboarding Dashboard'}
                    {currentStepData.mockView === 'employee_dashboard' && 'Employee Dashboard & Shift Summary'}
                    {currentStepData.mockView === 'submit_cash' && 'Submit Physical Cash & Jar Tips'}
                    {currentStepData.mockView === 'employee_approvals' && 'Digital Signature & Shift Verification'}
                    {currentStepData.mockView === 'file_dispute' && 'Dispute Management & Discrepancy Flagging'}
                    {currentStepData.mockView === 'pos_import' && 'Import POS Sales & Tips Batch'}
                    {currentStepData.mockView === 'review_approve' && 'Review & Pre-Approve Raw Tips'}
                    {currentStepData.mockView === 'manager_review' && 'Allocation Management & Dispute Overrides'}
                    {currentStepData.mockView === 'run_payroll' && 'Consolidated Tip Payroll Export'}
                    {currentStepData.mockView === 'user_management' && 'User Management & POS Register Mapping'}
                    {currentStepData.mockView === 'settings_studio' && 'General Settings & POS Configuration'}
                    {currentStepData.mockView === 'completed' && 'Tour Complete Checklist'}
                  </h4>
                  <p className="text-[8px] text-gray-500">Live Workspace Roster: 14 Active Employees • Bi-Weekly Pay Cycle</p>
                </div>
                <div className="hidden sm:flex items-center gap-1 bg-white border border-gray-200 px-2 py-1 rounded-lg text-[8px] font-bold text-gray-600">
                  <User size={10} className="text-gray-400" />
                  <span>Sarah Miller (Server)</span>
                </div>
              </div>

              {/* SIMULATOR RENDERING BODY */}
              <div className="flex-1 overflow-y-auto overflow-x-auto relative text-[10px] overscroll-y-contain">
                
                {/* 1. VIEW WELCOME */}
                {currentStepData.mockView === 'welcome' && (
                  <div className="flex flex-col items-center justify-center text-center h-full max-w-sm mx-auto space-y-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 shadow-md">
                      <Sparkles size={24} className="animate-spin-slow" />
                    </div>
                    <div className="space-y-1">
                      <h5 className="font-extrabold text-gray-900 text-[13px]">Welcome to TipValue AI</h5>
                      <p className="text-[9px] text-gray-500 leading-normal">
                        Experience the complete end-to-end tip management platform. Explore the live features on the left or allow the video simulation to guide you through each screen.
                      </p>
                    </div>
                    <button 
                      onClick={handleNext}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-4 py-2 rounded-xl shadow shadow-blue-500/25 flex items-center gap-1 cursor-pointer transition-all"
                    >
                      <span>Begin Interactive Tour</span>
                      <ArrowRight size={12} />
                    </button>
                  </div>
                )}

                {/* 2. EMPLOYEE DASHBOARD */}
                {currentStepData.mockView === 'employee_dashboard' && (
                  <div className="space-y-4">
                    {/* Stat Cards Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-2.5">
                      <div id="sim-dashboard-pos-card" className="bg-white p-2.5 border border-gray-150 rounded-xl shadow-xs">
                        <span className="text-[8px] uppercase tracking-wider text-gray-400 font-extrabold block">POS Credit Tips</span>
                        <div className="text-sm font-black text-blue-600 mt-1">$482.50</div>
                        <span className="text-[8px] text-emerald-600 font-bold">14 shifts recorded</span>
                      </div>
                      <div className="bg-white p-2.5 border border-gray-150 rounded-xl shadow-xs">
                        <span className="text-[8px] uppercase tracking-wider text-gray-400 font-extrabold block">Pooled Cash</span>
                        <div className="text-sm font-black text-emerald-600 mt-1">$125.00</div>
                        <span className="text-[8px] text-emerald-600 font-bold">In review</span>
                      </div>
                      <div id="sim-dashboard-jar-card" className="bg-white p-2.5 border border-gray-150 rounded-xl shadow-xs">
                        <span className="text-[8px] uppercase tracking-wider text-gray-400 font-extrabold block">Jar Tip Shares</span>
                        <div className="text-sm font-black text-purple-600 mt-1">$95.00</div>
                        <span className="text-[8px] text-gray-400">Monthly</span>
                      </div>
                    </div>

                    {/* Shifting log list */}
                    <div className="bg-white rounded-xl border border-gray-150 shadow-xs overflow-hidden">
                      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 font-bold text-[9px] text-gray-700">
                        Your Recent Shift Allocations
                      </div>
                      <div id="sim-dashboard-shift-log" className="divide-y divide-gray-100 text-[8px]">
                        <div className="flex justify-between items-center p-2">
                          <div className="font-bold text-gray-800">July 10, 2026 - Night Shift (Server)</div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-900 font-bold">$120.00 (POS)</span>
                            <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-black uppercase text-[7px]">Approved</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-amber-50/30">
                          <div className="font-bold text-gray-800">July 09, 2026 - Morning Shift (Server)</div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-900 font-bold">$95.00 (Cash)</span>
                            <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-black uppercase text-[7px]">Pending</span>
                            <span className="bg-amber-500 text-white font-extrabold px-1 rounded text-[7px]">Disputable</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-2">
                          <div className="font-bold text-gray-800">July 08, 2026 - All Day (Bartender)</div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-900 font-bold">$185.00 (POS)</span>
                            <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-black uppercase text-[7px]">Approved</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 3. SUBMIT CASH */}
                {currentStepData.mockView === 'submit_cash' && (
                  <div className="space-y-4">
                    <div className="flex border-b border-gray-200">
                      <button className={`px-3 py-1 font-bold ${subStep === 0 ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400'}`}>
                        Log Cash Tip
                      </button>
                      <button id="submit-cash-history-tab" className={`px-3 py-1 font-bold ${subStep === 1 ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400'}`}>
                        Submission History
                      </button>
                    </div>

                    {subStep === 0 ? (
                      <div className="bg-white p-3 rounded-xl border border-gray-150 shadow-xs space-y-2.5 max-w-sm mx-auto">
                        <div>
                          <label className="text-[8px] font-black uppercase text-gray-400 block mb-0.5">Roster Employee Name</label>
                          <input type="text" disabled value="Sarah Miller" className="bg-gray-100 border border-gray-250 w-full p-1.5 rounded-lg outline-none text-gray-700 font-medium" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[8px] font-black uppercase text-gray-400 block mb-0.5">Shift Block</label>
                            <input type="text" disabled value="Night Shift" className="bg-gray-100 border border-gray-250 w-full p-1.5 rounded-lg text-gray-700" />
                          </div>
                          <div>
                            <label className="text-[8px] font-black uppercase text-gray-400 block mb-0.5">Tip Amount ($)</label>
                            <input 
                              id="submit-cash-amount-input"
                              type="text" 
                              readOnly
                              placeholder="0.00" 
                              value={simulatedCashAmount}
                              className="bg-white border border-gray-250 w-full p-1.5 rounded-lg text-gray-900 outline-none focus:border-blue-500 font-mono" 
                            />
                          </div>
                        </div>
                        <button id="submit-cash-submit-button" className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold w-full py-2 rounded-xl text-[10px] shadow cursor-pointer transition-all">
                          Submit Cash Tip
                        </button>
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border border-gray-150 shadow-xs overflow-hidden animate-fade-in text-[8px]">
                        <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 font-bold text-gray-700">
                          Active Cash Tip Ledger
                        </div>
                        <div className="divide-y divide-gray-100">
                          <div className="flex justify-between items-center p-2 bg-amber-50/20">
                            <div>
                              <div className="font-bold text-gray-900">July 11, 2026 - Night Shift</div>
                              <span className="text-gray-400">Logged at 11:42 PM</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-black text-gray-900">$150.00</span>
                              <span className="bg-amber-100 text-amber-700 px-1 rounded uppercase font-extrabold text-[7px]">Pending</span>
                            </div>
                          </div>
                          <div className="flex justify-between items-center p-2">
                            <div>
                              <div className="font-bold text-gray-900">July 08, 2026 - Morning Shift</div>
                              <span className="text-gray-400">Verified by Shift Lead</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-black text-gray-900">$90.00</span>
                              <span className="bg-green-100 text-green-700 px-1 rounded uppercase font-extrabold text-[7px]">Approved</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 4. EMPLOYEE APPROVALS & E-SIGNATURE */}
                {currentStepData.mockView === 'employee_approvals' && (
                  <div className="space-y-4">
                    <div id="sim-approvals-period-card" className="bg-white p-3 rounded-xl border border-gray-150 shadow-xs space-y-2">
                      <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                        <div>
                          <span className="font-bold text-gray-900 text-[10px]">Official Pay Period: July 1 - July 15, 2026</span>
                          <p className="text-[7px] text-gray-500">12 Shifts Total • $702.50 Verified Gross Tips</p>
                        </div>
                        {subStep === 2 || simulatedSignatureDone ? (
                          <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-black text-[7px] flex items-center gap-1">
                            <Check size={10} /> Signed & Verified
                          </span>
                        ) : (
                          <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-black text-[7px]">
                            Pending Signature
                          </span>
                        )}
                      </div>

                      <div className="divide-y divide-gray-100 text-[8px]">
                        <div className="flex justify-between py-1">
                          <span className="text-gray-600">POS Credit Card Tips</span>
                          <span className="font-bold text-gray-900">$482.50</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-gray-600">Cash Tips Reported</span>
                          <span className="font-bold text-gray-900">$125.00</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-gray-600">Shared Jar Allocation</span>
                          <span className="font-bold text-gray-900">$95.00</span>
                        </div>
                      </div>

                      {subStep === 0 && !simulatedSignatureDone && (
                        <button id="sim-approvals-sign-button" className="bg-blue-600 text-white font-extrabold w-full py-2 rounded-xl text-[9px] shadow cursor-pointer flex items-center justify-center gap-1.5">
                          <Fingerprint size={12} />
                          <span>Review & Sign Off Shift Tips</span>
                        </button>
                      )}
                    </div>

                    {subStep === 1 && (
                      <div className="bg-white p-3 rounded-xl border border-blue-200 shadow-md space-y-2 max-w-sm mx-auto animate-fade-in">
                        <div className="text-center pb-1 border-b border-gray-100">
                          <h6 className="font-black text-gray-900 text-[10px]">IRS Compliance Digital Signature</h6>
                          <p className="text-[7px] text-gray-500">I certify that this tip summary accurately represents my allocations.</p>
                        </div>
                        
                        <div className="h-16 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                          <span className="font-serif italic text-blue-700 text-lg font-bold">Sarah Miller</span>
                        </div>

                        <button id="sim-approvals-confirm-sign" className="bg-emerald-600 text-white font-extrabold w-full py-2 rounded-xl text-[9px] shadow cursor-pointer">
                          Confirm & Submit Digital Signature
                        </button>
                      </div>
                    )}

                    {subStep === 2 && (
                      <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-center text-[8px] text-emerald-900 space-y-0.5 animate-fade-in">
                        <span className="font-extrabold block">✓ Shift Records Locked & Certified</span>
                        <p className="text-emerald-700">Digital signature hash recorded in audit log: #SIG-8921-VERIFIED</p>
                      </div>
                    )}
                  </div>
                )}

                {/* 5. FILE DISPUTE */}
                {currentStepData.mockView === 'file_dispute' && (
                  <div className="space-y-4">
                    {subStep === 0 && (
                      <div className="bg-white rounded-xl border border-gray-150 shadow-xs overflow-hidden text-[8px]">
                        <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 font-bold text-gray-700">
                          Disputable Shift Allocations
                        </div>
                        <div className="p-3 flex justify-between items-center bg-amber-50">
                          <div>
                            <div className="font-extrabold text-gray-900">July 09, 2026 - Morning (Sarah Miller)</div>
                            <span className="text-gray-500">Allocated Pool: $95.00 (Cash) • Recorded: 5 hrs</span>
                          </div>
                          <button id="sim-file-dispute-trigger" className="bg-amber-500 text-white px-2 py-1 rounded font-black uppercase text-[7px] cursor-pointer">
                            File Dispute
                          </button>
                        </div>
                      </div>
                    )}

                    {subStep === 1 && (
                      <div className="bg-white p-3 rounded-xl border border-gray-150 shadow-xs space-y-2 max-w-sm mx-auto animate-fade-in">
                        <div className="text-center pb-1">
                          <h6 className="font-black text-rose-600 text-[10px]">Dispute Submission Form</h6>
                          <p className="text-[7px] text-gray-400">Flagging: July 09 Morning Cash Allocation ($95.00)</p>
                        </div>
                        <div>
                          <label className="text-[7px] font-black uppercase text-gray-400 block">Correction Category</label>
                          <input type="text" disabled value="Hours / Shift Discrepancy" className="bg-gray-100 border border-gray-200 w-full p-1.5 rounded-md text-gray-700" />
                        </div>
                        <div>
                          <label className="text-[7px] font-black uppercase text-gray-400 block">Dispute Explanation</label>
                          <input 
                            id="sim-dispute-reason-input"
                            type="text" 
                            readOnly 
                            placeholder="State the discrepancy..." 
                            value={simulatedDisputeReason}
                            className="bg-white border border-gray-250 w-full p-1.5 rounded-md text-gray-900 outline-none text-[8px]" 
                          />
                        </div>
                        <button id="sim-submit-dispute-button" className="bg-rose-600 text-white font-extrabold w-full py-1.5 rounded-lg text-[9px] cursor-pointer">
                          Submit Dispute to Managers
                        </button>
                      </div>
                    )}

                    {subStep === 2 && (
                      <div className="bg-white rounded-xl border border-gray-150 shadow-xs overflow-hidden text-[8px] animate-fade-in">
                        <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 font-bold text-gray-700">
                          Active Dispute Tracking
                        </div>
                        <div className="p-3 flex justify-between items-center bg-rose-50 border-l-4 border-rose-500">
                          <div>
                            <div className="font-extrabold text-gray-900">July 09, 2026 - Morning Shift</div>
                            <span className="text-rose-600 font-bold">Disputed: "Hours incorrect on night shift (worked 8 hrs)"</span>
                          </div>
                          <span className="bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-black uppercase text-[7px] animate-pulse">
                            Under Review
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 6. IMPORT POS TIPS */}
                {currentStepData.mockView === 'pos_import' && (
                  <div className="space-y-4">
                    <div id="sim-pos-dropzone" className="border-2 border-dashed border-blue-400/50 bg-blue-50/50 rounded-2xl p-4 text-center space-y-2 cursor-pointer">
                      <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center mx-auto">
                        <Upload size={16} />
                      </div>
                      <div>
                        <span className="font-extrabold text-gray-900 text-[10px] block">Drop Toast or Square POS Report</span>
                        <p className="text-[7px] text-gray-500">Supports .csv, .xlsx, .tsv (Auto-maps Employee POS IDs)</p>
                      </div>
                      {subStep >= 1 && (
                        <div className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[8px] font-bold px-2 py-0.5 rounded-md">
                          <Check size={10} /> Toast_Shift_Report_July11.csv (42 rows)
                        </div>
                      )}
                    </div>

                    {subStep >= 1 && (
                      <div className="bg-white rounded-xl border border-gray-150 overflow-hidden text-[8px] animate-fade-in">
                        <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 font-bold text-gray-700 flex justify-between">
                          <span>POS Import Preview</span>
                          <span className="text-blue-600 font-mono">42 Transactions ($1,480.00)</span>
                        </div>
                        <div className="divide-y divide-gray-100">
                          <div className="flex justify-between p-2">
                            <span>POS-101 (Sarah Miller)</span>
                            <span className="font-bold text-gray-900">$182.50 (Toast Card Tip)</span>
                          </div>
                          <div className="flex justify-between p-2">
                            <span>POS-104 (Alice Vance)</span>
                            <span className="font-bold text-gray-900">$140.00 (Toast Card Tip)</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {subStep === 1 && (
                      <button id="sim-pos-process-button" className="bg-blue-600 text-white font-extrabold w-full py-2 rounded-xl text-[9px] shadow cursor-pointer">
                        Process and Assign 42 POS Records
                      </button>
                    )}

                    {subStep === 2 && (
                      <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-center text-[8px] text-emerald-900 space-y-0.5 animate-fade-in">
                        <span className="font-extrabold block">✓ Batch Successfully Imported</span>
                        <p className="text-emerald-700">All 42 transactions mapped to active roster employees.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* 7. REVIEW & APPROVE TIPS */}
                {currentStepData.mockView === 'review_approve' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center bg-white p-2.5 border border-gray-150 rounded-xl">
                      <div>
                        <span className="font-extrabold text-gray-900 text-[10px]">Pending Tip Queue: 6 Items</span>
                      </div>
                      <button id="sim-review-approve-all-btn" className="bg-emerald-600 text-white font-black text-[8px] px-2.5 py-1.5 rounded-lg cursor-pointer">
                        Approve All 6 Tips
                      </button>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-150 overflow-hidden text-[8px]">
                      <div className="grid grid-cols-12 gap-1 bg-gray-50 p-2 border-b border-gray-100 text-gray-500 font-extrabold">
                        <div className="col-span-4">Roster Member</div>
                        <div className="col-span-3">Source</div>
                        <div className="col-span-3">Amount</div>
                        <div className="col-span-2 text-right">Status</div>
                      </div>
                      <div className="divide-y divide-gray-100">
                        <div className={`grid grid-cols-12 gap-1 p-2 items-center ${subStep >= 1 ? 'bg-emerald-50/30' : ''}`}>
                          <div className="col-span-4 font-bold text-gray-900">Sarah Miller</div>
                          <div className="col-span-3"><span className="bg-blue-100 text-blue-800 px-1 rounded text-[6px] font-bold">POS Card</span></div>
                          <div className="col-span-3 font-mono font-bold">$182.50</div>
                          <div className="col-span-2 text-right">
                            {subStep >= 1 ? <span className="text-emerald-600 font-bold">Approved</span> : <span className="text-amber-600 font-bold">Pending</span>}
                          </div>
                        </div>
                        <div className={`grid grid-cols-12 gap-1 p-2 items-center ${subStep >= 1 ? 'bg-emerald-50/30' : ''}`}>
                          <div className="col-span-4 font-bold text-gray-900">Alice Vance</div>
                          <div className="col-span-3"><span className="bg-emerald-100 text-emerald-800 px-1 rounded text-[6px] font-bold">Cash</span></div>
                          <div className="col-span-3 font-mono font-bold">$120.00</div>
                          <div className="col-span-2 text-right">
                            {subStep >= 1 ? <span className="text-emerald-600 font-bold">Approved</span> : <span className="text-amber-600 font-bold">Pending</span>}
                          </div>
                        </div>
                        <div className={`grid grid-cols-12 gap-1 p-2 items-center ${subStep >= 1 ? 'bg-emerald-50/30' : ''}`}>
                          <div className="col-span-4 font-bold text-gray-900">David King</div>
                          <div className="col-span-3"><span className="bg-purple-100 text-purple-800 px-1 rounded text-[6px] font-bold">Shared Jar</span></div>
                          <div className="col-span-3 font-mono font-bold">$95.00</div>
                          <div className="col-span-2 text-right">
                            {subStep >= 1 ? <span className="text-emerald-600 font-bold">Approved</span> : <span className="text-amber-600 font-bold">Pending</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 8. REVIEW ALLOCATIONS & DISPUTES */}
                {currentStepData.mockView === 'manager_review' && (
                  <div className="space-y-4">
                    <div className="flex gap-4 border-b border-gray-200">
                      <button className="px-3 py-1 font-bold text-blue-600 border-b-2 border-blue-600">Shift Pools</button>
                      <button className="px-3 py-1 font-bold text-gray-400">Dispute Console (1)</button>
                    </div>

                    <div className="bg-white rounded-xl border border-rose-150 overflow-hidden text-[8px] p-3 space-y-2">
                      <div className="flex justify-between items-center text-rose-700 font-bold border-b border-rose-100 pb-1.5">
                        <span className="flex items-center gap-1"><AlertCircle size={10} /> Active Dispute: Sarah Miller</span>
                        <span className="bg-rose-100 text-rose-800 px-1 rounded text-[7px]">Needs Action</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-gray-600">
                        <div><strong>Original:</strong> $95.00 (5 hrs recorded)</div>
                        <div><strong>Claimed:</strong> 8 hrs worked on night shift</div>
                      </div>
                      {subStep === 0 && (
                        <button id="sim-dispute-resolve-btn" className="bg-amber-500 text-white font-extrabold w-full py-1.5 rounded-lg cursor-pointer">
                          Investigate & Override Shift
                        </button>
                      )}
                    </div>

                    {subStep >= 1 && (
                      <div className="bg-white p-3 rounded-xl border border-emerald-200 shadow-xs space-y-2 max-w-sm mx-auto animate-fade-in">
                        <h6 className="font-extrabold text-gray-900 text-[9px] text-center border-b border-gray-100 pb-1">Manager Override Confirmation</h6>
                        <div className="flex justify-between items-center text-[8px]">
                          <span>Corrected Amount:</span>
                          <span className="font-bold text-emerald-700 font-mono text-[10px]">$195.00</span>
                        </div>
                        {subStep === 1 ? (
                          <button id="sim-dispute-approve-override-btn" className="bg-emerald-600 text-white font-extrabold w-full py-1.5 rounded-lg text-[8px] cursor-pointer">
                            Approve Override ($195.00)
                          </button>
                        ) : (
                          <div className="p-2 bg-emerald-50 text-emerald-900 text-center font-bold text-[7px] rounded-lg">
                            ✓ Override approved & locked for payroll export
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* 9. RUN PAYROLL */}
                {currentStepData.mockView === 'run_payroll' && (
                  <div className="space-y-4">
                    {subStep === 0 && (
                      <div className="bg-white p-3 rounded-xl border border-gray-150 space-y-3 text-center max-w-sm mx-auto">
                        <div className="space-y-1">
                          <span className="font-bold text-gray-800 text-[11px] block">Run Tip Payroll Cycle</span>
                          <p className="text-gray-400 text-[8px]">July 1, 2026 - July 15, 2026 (Bi-Weekly Cycle)</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-150 p-2 rounded-lg text-left text-[8px] text-blue-800 flex justify-between items-center">
                          <span>Verified Shifts: 48 Line Items</span>
                          <span className="bg-blue-600 text-white font-bold px-1.5 rounded">All Clear</span>
                        </div>
                        <button id="sim-payroll-export-button" className="bg-blue-600 text-white font-black w-full py-2 rounded-xl text-[10px] shadow cursor-pointer">
                          Generate Gusto / ADP Tip Payroll CSV
                        </button>
                      </div>
                    )}

                    {subStep === 1 && (
                      <div className="flex flex-col items-center justify-center h-28 space-y-2 animate-pulse">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-[8px] font-bold text-blue-600">Consolidating tip allocations across 14 roster members...</span>
                      </div>
                    )}

                    {(subStep === 2 || subStep === 3) && (
                      <div className="bg-white p-3 rounded-xl border border-gray-150 space-y-3 max-w-sm mx-auto animate-fade-in">
                        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 p-3 rounded-xl">
                          <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 shrink-0">
                            <FileDown size={18} />
                          </div>
                          <div className="flex-1">
                            <span className="font-bold text-emerald-950 text-[10px] block">TipValue_Payroll_July2026.csv</span>
                            <span className="text-[8px] text-emerald-600">CSV formatted for Gusto/ADP/Paychex</span>
                          </div>
                        </div>

                        <button id="sim-payroll-download-button" className="bg-emerald-600 text-white font-extrabold w-full py-2 rounded-xl text-[10px] shadow flex items-center justify-center gap-1 cursor-pointer">
                          <Download size={12} />
                          <span>Download Export File</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* 10. USER MANAGEMENT */}
                {currentStepData.mockView === 'user_management' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center bg-white p-3 border border-gray-150 rounded-xl shadow-xs">
                      <span className="font-extrabold text-gray-800 text-[10px]">Staff Roster: 14 Active Accounts</span>
                      <button id="sim-add-staff-button" className="bg-blue-600 text-white font-bold text-[8px] px-2.5 py-1 rounded-lg cursor-pointer">
                        + Add Staff
                      </button>
                    </div>

                    {subStep === 1 && (
                      <div className="bg-white p-4 rounded-xl border border-gray-150 shadow-md space-y-3 max-w-md mx-auto animate-fade-in">
                        <h6 className="font-black text-gray-900 text-[10px] text-center border-b border-gray-100 pb-1.5">Add Team Member</h6>
                        <div className="space-y-2 text-[8px]">
                          <div>
                            <label className="text-[7px] uppercase font-bold text-gray-400 block mb-0.5">Full Name</label>
                            <input id="staff-name-input" type="text" readOnly value={simulatedStaffName} placeholder="Enter full name..." className="bg-white border border-gray-250 w-full p-1.5 rounded-lg" />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[7px] uppercase font-bold text-gray-400 block mb-0.5">Role Point Weight</label>
                              <input type="text" disabled value="Bartender (1.2 Points)" className="bg-gray-100 border border-gray-250 w-full p-1.5 rounded-lg text-gray-600" />
                            </div>
                            <div>
                              <label className="text-[7px] uppercase font-bold text-gray-400 block mb-0.5">Register POS ID</label>
                              <input id="staff-pos-id-input" type="text" readOnly value={simulatedStaffPosId} placeholder="POS Code..." className="bg-white border border-gray-250 w-full p-1.5 rounded-lg font-mono text-blue-600 font-bold" />
                            </div>
                          </div>
                        </div>
                        <button id="staff-save-button" className="bg-emerald-600 text-white font-extrabold w-full py-1.5 rounded-xl text-[9px] cursor-pointer">
                          Save & Link POS ID
                        </button>
                      </div>
                    )}

                    {(subStep === 0 || subStep === 2) && (
                      <div className="bg-white rounded-xl border border-gray-150 overflow-hidden text-[8px]">
                        <div className="grid grid-cols-12 gap-1 bg-gray-50 p-2 border-b border-gray-100 font-bold text-gray-600">
                          <div className="col-span-4">Employee Name</div>
                          <div className="col-span-4">Role & Points</div>
                          <div className="col-span-4">Linked POS ID</div>
                        </div>
                        <div className="divide-y divide-gray-150">
                          <div className="grid grid-cols-12 gap-1 p-2">
                            <div className="col-span-4 font-bold text-gray-900">Sarah Miller</div>
                            <div className="col-span-4">Server (1.0x)</div>
                            <div className="col-span-4 font-mono text-blue-600 font-bold">POS-101</div>
                          </div>
                          <div className="grid grid-cols-12 gap-1 p-2">
                            <div className="col-span-4 font-bold text-gray-900">Alice Vance</div>
                            <div className="col-span-4">Server (1.0x)</div>
                            <div className="col-span-4 font-mono text-blue-600 font-bold">POS-104</div>
                          </div>
                          {subStep === 2 && (
                            <div className="grid grid-cols-12 gap-1 p-2 bg-emerald-50 font-bold text-emerald-900 animate-fade-in">
                              <div className="col-span-4">John Doe</div>
                              <div className="col-span-4">Bartender (1.2x)</div>
                              <div className="col-span-4 font-mono text-emerald-700">POS-998</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 11. DETAILED SETTINGS STUDIO WITH VERIFIED ARCHITECTURE */}
                {currentStepData.mockView === 'settings_studio' && (
                  <div className="flex flex-col h-full bg-white rounded-xl border border-gray-150 overflow-hidden shadow-xs">
                    
                    {/* Settings Top Header Bar */}
                    <div className="bg-gray-50 border-b border-gray-150 px-3 py-2 flex justify-between items-center shrink-0">
                      <div className="flex items-center gap-1.5">
                        <Sliders size={12} className="text-purple-600" />
                        <span className="font-black text-gray-900 text-[10px]">Settings & Configuration Hub</span>
                      </div>
                      <span className="text-[7px] font-extrabold uppercase bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded border border-purple-200">
                        Admin Access Only
                      </span>
                    </div>

                    {/* Settings Split Layout: Left Nav + Right Content */}
                    <div className="flex flex-1 overflow-hidden">
                      
                      {/* Left: Verified Settings Sidebar Categories */}
                      <div className="w-36 bg-gray-50/80 border-r border-gray-150 p-1.5 flex flex-col justify-between overflow-y-auto shrink-0 select-none text-[8px]">
                        <div className="space-y-2.5">
                          
                          {/* Category 1: Operations & Setup */}
                          <div className="space-y-0.5">
                            <div className="text-[7px] font-black uppercase tracking-wider text-gray-400 px-1 py-0.5">
                              1. Operations & Setup
                            </div>
                            <button
                              id="sim-settings-sec-general-pos"
                              onClick={() => setSettingsActiveSection('general_pos')}
                              className={`w-full text-left px-1.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                settingsActiveSection === 'general_pos' ? 'bg-purple-600 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200/60'
                              }`}
                            >
                              <Sliders size={10} className="shrink-0" />
                              <span className="truncate">General & POS</span>
                            </button>
                            <button
                              id="sim-settings-sec-shifts-hours"
                              onClick={() => setSettingsActiveSection('shifts_hours')}
                              className={`w-full text-left px-1.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                settingsActiveSection === 'shifts_hours' ? 'bg-purple-600 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200/60'
                              }`}
                            >
                              <Clock size={10} className="shrink-0" />
                              <span className="truncate">Shifts & Hours</span>
                            </button>
                            <button
                              id="sim-settings-sec-staff-roster"
                              onClick={() => setSettingsActiveSection('staff_roster')}
                              className={`w-full text-left px-1.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                settingsActiveSection === 'staff_roster' ? 'bg-purple-600 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200/60'
                              }`}
                            >
                              <Users size={10} className="shrink-0" />
                              <span className="truncate">Staff & Roster</span>
                            </button>
                            <button
                              id="sim-settings-sec-tip-allocations"
                              onClick={() => setSettingsActiveSection('tip_allocations')}
                              className={`w-full text-left px-1.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                settingsActiveSection === 'tip_allocations' ? 'bg-purple-600 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200/60'
                              }`}
                            >
                              <Layers size={10} className="shrink-0" />
                              <span className="truncate">Tip Allocations</span>
                            </button>
                            <button
                              id="sim-settings-sec-business-profile"
                              onClick={() => setSettingsActiveSection('business_profile')}
                              className={`w-full text-left px-1.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                settingsActiveSection === 'business_profile' ? 'bg-purple-600 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200/60'
                              }`}
                            >
                              <Building2 size={10} className="shrink-0" />
                              <span className="truncate">Business Profile</span>
                            </button>
                          </div>

                          {/* Category 2: Billing & Growth */}
                          <div className="space-y-0.5">
                            <div className="text-[7px] font-black uppercase tracking-wider text-gray-400 px-1 py-0.5">
                              2. Billing & Growth
                            </div>
                            <button
                              id="sim-settings-sec-billing"
                              onClick={() => setSettingsActiveSection('subscription_billing')}
                              className={`w-full text-left px-1.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                settingsActiveSection === 'subscription_billing' ? 'bg-purple-600 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200/60'
                              }`}
                            >
                              <CreditCard size={10} className="shrink-0" />
                              <span className="truncate">Subscription & Billing</span>
                            </button>
                            <button
                              id="sim-settings-sec-referral"
                              onClick={() => setSettingsActiveSection('referral_program')}
                              className={`w-full text-left px-1.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                settingsActiveSection === 'referral_program' ? 'bg-purple-600 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200/60'
                              }`}
                            >
                              <Award size={10} className="shrink-0" />
                              <span className="truncate">Referral Program</span>
                            </button>
                          </div>

                          {/* Category 3: Support & Docs */}
                          <div className="space-y-0.5">
                            <div className="text-[7px] font-black uppercase tracking-wider text-gray-400 px-1 py-0.5">
                              3. Support & Docs
                            </div>
                            <button
                              id="sim-settings-sec-guide"
                              onClick={() => setSettingsActiveSection('tipflow_guide')}
                              className={`w-full text-left px-1.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                settingsActiveSection === 'tipflow_guide' ? 'bg-purple-600 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200/60'
                              }`}
                            >
                              <BookOpen size={10} className="shrink-0" />
                              <span className="truncate">TipFlow Guide (5 Tabs)</span>
                            </button>
                            <button
                              id="sim-settings-sec-support"
                              onClick={() => setSettingsActiveSection('contact_support')}
                              className={`w-full text-left px-1.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                settingsActiveSection === 'contact_support' ? 'bg-purple-600 text-white shadow-xs' : 'text-gray-600 hover:bg-gray-200/60'
                              }`}
                            >
                              <HelpCircle size={10} className="shrink-0" />
                              <span className="truncate">Contact Support</span>
                            </button>
                          </div>

                        </div>
                      </div>

                      {/* Right: Section Content View */}
                      <div className="flex-1 p-3 overflow-y-auto space-y-3 bg-white text-gray-800">
                        
                        {/* ======================================================= */}
                        {/* 1.1 GENERAL & POS SETTINGS (WITH 4 DEDICATED SUB-TABS)  */}
                        {/* ======================================================= */}
                        {settingsActiveSection === 'general_pos' && (
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="font-extrabold text-gray-900 text-[10px]">General & POS Settings</span>
                              <span className="text-[7px] text-gray-400">4 Sub-Tabs</span>
                            </div>

                            {/* 4-Sub-Tab Navigation Header */}
                            <div className="flex border-b border-gray-200 overflow-x-auto text-[8px]">
                              <button 
                                id="sim-settings-tab-pos"
                                onClick={() => setSettingsActiveSubTab('pos')}
                                className={`px-2 py-1.5 font-bold cursor-pointer transition-all whitespace-nowrap ${
                                  settingsActiveSubTab === 'pos' ? 'text-purple-700 border-b-2 border-purple-600 font-black' : 'text-gray-400 hover:text-gray-600'
                                }`}
                              >
                                POS Settings
                              </button>
                              <button 
                                id="sim-settings-tab-service-fees"
                                onClick={() => setSettingsActiveSubTab('service_fees')}
                                className={`px-2 py-1.5 font-bold cursor-pointer transition-all whitespace-nowrap ${
                                  settingsActiveSubTab === 'service_fees' ? 'text-purple-700 border-b-2 border-purple-600 font-black' : 'text-gray-400 hover:text-gray-600'
                                }`}
                              >
                                Service Fees & CC Offsets
                              </button>
                              <button 
                                id="sim-settings-tab-payroll"
                                onClick={() => setSettingsActiveSubTab('payroll')}
                                className={`px-2 py-1.5 font-bold cursor-pointer transition-all whitespace-nowrap ${
                                  settingsActiveSubTab === 'payroll' ? 'text-purple-700 border-b-2 border-purple-600 font-black' : 'text-gray-400 hover:text-gray-600'
                                }`}
                              >
                                Payroll Settings
                              </button>
                              <button 
                                id="sim-settings-tab-irs"
                                onClick={() => setSettingsActiveSubTab('irs_8027')}
                                className={`px-2 py-1.5 font-bold cursor-pointer transition-all whitespace-nowrap ${
                                  settingsActiveSubTab === 'irs_8027' ? 'text-purple-700 border-b-2 border-purple-600 font-black' : 'text-gray-400 hover:text-gray-600'
                                }`}
                              >
                                IRS Form 8027
                              </button>
                            </div>

                            {/* Sub-Tab 1: POS Settings */}
                            {settingsActiveSubTab === 'pos' && (
                              <div className="bg-gray-50/70 p-2.5 rounded-xl border border-gray-150 space-y-2">
                                <span className="font-bold text-gray-800 text-[8px] block">Supported POS Integrations & Sync</span>
                                <div className="grid grid-cols-3 gap-1.5">
                                  {['toast', 'square', 'clover', 'aloha', 'lightspeed'].map((pos) => (
                                    <button
                                      key={pos}
                                      onClick={() => setSettingsSelectedPos(pos)}
                                      className={`p-1.5 rounded-lg border text-center uppercase font-black text-[7px] cursor-pointer transition-all ${
                                        settingsSelectedPos === pos ? 'border-purple-600 bg-purple-50 text-purple-700 shadow-xs' : 'border-gray-200 text-gray-600 hover:bg-white'
                                      }`}
                                    >
                                      {pos}
                                    </button>
                                  ))}
                                </div>
                                <div className="text-[7px] text-gray-500 bg-white p-2 rounded-lg leading-relaxed border border-gray-150">
                                  ✓ Auto-syncs daily server gross sales, guest counts, and credit card tip batches at 4:00 AM shift reset.
                                </div>
                              </div>
                            )}

                            {/* Sub-Tab 2: Service Fees & CC Offsets */}
                            {settingsActiveSubTab === 'service_fees' && (
                              <div className="bg-gray-50/70 p-2.5 rounded-xl border border-gray-150 space-y-2">
                                <span className="font-bold text-gray-800 text-[8px] block">Mandatory Service Fees & CC Deductions</span>
                                <div className="flex justify-between items-center p-2 bg-white rounded-lg text-[8px] border border-gray-150">
                                  <div>
                                    <span className="font-bold text-gray-800 block">Auto-Grat / Service Charge Distribution</span>
                                    <span className="text-[7px] text-gray-500">80% to Service Pool • 20% to Kitchen Roster</span>
                                  </div>
                                  <span className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded font-bold text-[7px]">Configured</span>
                                </div>
                                <div className="flex justify-between items-center p-2 bg-white rounded-lg text-[8px] border border-gray-150">
                                  <div>
                                    <span className="font-bold text-gray-800 block">Credit Card Processing Fee Deduction</span>
                                    <span className="text-[7px] text-amber-700">Auto-locked in CA/MA/ME for state compliance</span>
                                  </div>
                                  <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-bold text-[7px]">State Protected</span>
                                </div>
                              </div>
                            )}

                            {/* Sub-Tab 3: Payroll Settings */}
                            {settingsActiveSubTab === 'payroll' && (
                              <div className="bg-gray-50/70 p-2.5 rounded-xl border border-gray-150 space-y-2">
                                <span className="font-bold text-gray-800 text-[8px] block">Payroll Provider Presets & Anchor Schedule</span>
                                <div className="grid grid-cols-3 gap-1.5">
                                  {['gusto', 'adp', 'paychex'].map((prov) => (
                                    <button
                                      key={prov}
                                      onClick={() => setSettingsSelectedPayroll(prov)}
                                      className={`p-1.5 rounded-lg border text-center uppercase font-black text-[7px] cursor-pointer transition-all ${
                                        settingsSelectedPayroll === prov ? 'border-purple-600 bg-purple-50 text-purple-700 shadow-xs' : 'border-gray-200 text-gray-600 hover:bg-white'
                                      }`}
                                    >
                                      {prov}
                                    </button>
                                  ))}
                                </div>
                                <div className="grid grid-cols-2 gap-1.5 text-[8px]">
                                  <div className="bg-white p-2 rounded-lg border border-gray-150">
                                    <span className="text-[7px] text-gray-400 block uppercase font-bold">Pay Frequency</span>
                                    <span className="font-bold text-gray-800">Bi-Weekly (14 Days)</span>
                                  </div>
                                  <div className="bg-white p-2 rounded-lg border border-gray-150">
                                    <span className="text-[7px] text-gray-400 block uppercase font-bold">Next Cycle Anchor</span>
                                    <span className="font-bold text-gray-800">July 15, 2026</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Sub-Tab 4: IRS Form 8027 */}
                            {settingsActiveSubTab === 'irs_8027' && (
                              <div className="bg-gray-50/70 p-2.5 rounded-xl border border-gray-150 space-y-2 animate-fade-in">
                                <div className="flex justify-between items-center border-b border-gray-200 pb-1">
                                  <span className="font-bold text-gray-900 text-[8px]">IRS Form 8027 (8% Shortfall Calculation)</span>
                                  <span className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded font-black text-[7px] uppercase">Active (&gt;10 Staff)</span>
                                </div>
                                <div className="p-2 bg-white border border-purple-200 rounded-lg text-[7px] text-purple-950 space-y-1">
                                  <div className="flex justify-between">
                                    <span>Gross Food & Beverage Receipts:</span>
                                    <span className="font-mono font-bold">$18,400.00</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>IRS 8% Minimum Tip Requirement:</span>
                                    <span className="font-mono font-bold">$1,472.00</span>
                                  </div>
                                  <div className="flex justify-between text-emerald-800 font-bold border-t border-purple-100 pt-1">
                                    <span>Reported Tips ($1,820.50):</span>
                                    <span>✓ Above Threshold (No Shortfall)</span>
                                  </div>
                                </div>
                              </div>
                            )}

                          </div>
                        )}

                        {/* ======================================================= */}
                        {/* 1.2 SHIFTS & HOURS CONFIGURATION                         */}
                        {/* ======================================================= */}
                        {settingsActiveSection === 'shifts_hours' && (
                          <div className="space-y-2.5 animate-fade-in">
                            <div className="flex justify-between items-center">
                              <span className="font-extrabold text-gray-900 text-[10px]">Shifts & Operating Schedule</span>
                              <span className="text-[7px] bg-blue-100 text-blue-800 font-bold px-1.5 py-0.5 rounded">3 Active Blocks</span>
                            </div>
                            <div className="space-y-1.5 text-[8px]">
                              <div className="p-2 bg-gray-50 border border-gray-150 rounded-lg flex justify-between items-center">
                                <div>
                                  <span className="font-bold text-gray-900 block">Lunch Shift Block</span>
                                  <span className="text-[7px] text-gray-500">10:00 AM – 4:00 PM • Shift Code: LUNCH</span>
                                </div>
                                <span className="bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded text-[7px]">Active</span>
                              </div>
                              <div className="p-2 bg-gray-50 border border-gray-150 rounded-lg flex justify-between items-center">
                                <div>
                                  <span className="font-bold text-gray-900 block">Dinner Shift Block</span>
                                  <span className="text-[7px] text-gray-500">4:00 PM – 12:00 AM • Shift Code: DINNER</span>
                                </div>
                                <span className="bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded text-[7px]">Active</span>
                              </div>
                              <div className="p-2 bg-gray-50 border border-gray-150 rounded-lg flex justify-between items-center">
                                <div>
                                  <span className="font-bold text-gray-900 block">Late Night Block</span>
                                  <span className="text-[7px] text-gray-500">12:00 AM – 4:00 AM • Shift Code: LATE</span>
                                </div>
                                <span className="bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded text-[7px]">Active</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ======================================================= */}
                        {/* 1.3 STAFF & ROSTER CONFIGURATION                         */}
                        {/* ======================================================= */}
                        {settingsActiveSection === 'staff_roster' && (
                          <div className="space-y-2.5 animate-fade-in">
                            <div className="flex justify-between items-center">
                              <span className="font-extrabold text-gray-900 text-[10px]">Staff Categories & Point Weightings</span>
                              <span className="text-[7px] bg-purple-100 text-purple-800 font-bold px-1.5 py-0.5 rounded">Roster Weighted</span>
                            </div>
                            <div className="divide-y divide-gray-150 border border-gray-150 rounded-lg text-[8px] overflow-hidden">
                              <div className="flex justify-between items-center p-1.5 bg-gray-50">
                                <span className="font-bold text-gray-800">Bartender (Front of House)</span>
                                <span className="font-mono font-bold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200">1.2x Points</span>
                              </div>
                              <div className="flex justify-between items-center p-1.5 bg-white">
                                <span className="font-bold text-gray-800">Lead Server</span>
                                <span className="font-mono font-bold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200">1.1x Points</span>
                              </div>
                              <div className="flex justify-between items-center p-1.5 bg-white">
                                <span className="font-bold text-gray-800">Server</span>
                                <span className="font-mono font-bold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200">1.0x Points</span>
                              </div>
                              <div className="flex justify-between items-center p-1.5 bg-white">
                                <span className="font-bold text-gray-800">Busser / Barback</span>
                                <span className="font-mono font-bold text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">0.6x Points</span>
                              </div>
                              <div className="flex justify-between items-center p-1.5 bg-white">
                                <span className="font-bold text-gray-800">Food Runner</span>
                                <span className="font-mono font-bold text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">0.5x Points</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ======================================================= */}
                        {/* 1.4 TIP ALLOCATIONS & RULES ENGINE                       */}
                        {/* ======================================================= */}
                        {settingsActiveSection === 'tip_allocations' && (
                          <div className="space-y-2.5 animate-fade-in">
                            <span className="font-extrabold text-gray-900 text-[10px] block">Tip Allocation & Pool Distribution Logic</span>
                            <div className="space-y-1.5 text-[8px]">
                              <button
                                onClick={() => setSettingsAllocationRule('hours_weighted')}
                                className={`w-full text-left p-2 rounded-lg border transition-all cursor-pointer ${
                                  settingsAllocationRule === 'hours_weighted' ? 'border-purple-600 bg-purple-50/60' : 'border-gray-200 bg-white'
                                }`}
                              >
                                <div className="flex justify-between items-center mb-0.5">
                                  <span className="font-bold text-gray-900">1. Hours-Weighted Pool (Recommended)</span>
                                  {settingsAllocationRule === 'hours_weighted' && <Check size={10} className="text-purple-700" />}
                                </div>
                                <p className="text-[7px] text-gray-500 leading-tight">
                                  Tips distributed proportionally based on exact clock-in/clock-out times during the active shift.
                                </p>
                              </button>

                              <button
                                onClick={() => setSettingsAllocationRule('point_based')}
                                className={`w-full text-left p-2 rounded-lg border transition-all cursor-pointer ${
                                  settingsAllocationRule === 'point_based' ? 'border-purple-600 bg-purple-50/60' : 'border-gray-200 bg-white'
                                }`}
                              >
                                <div className="flex justify-between items-center mb-0.5">
                                  <span className="font-bold text-gray-900">2. Role Point-Weighted System</span>
                                  {settingsAllocationRule === 'point_based' && <Check size={10} className="text-purple-700" />}
                                </div>
                                <p className="text-[7px] text-gray-500 leading-tight">
                                  Calculates total points = (Hours × Role Weight) before allocating pooled tips across roster.
                                </p>
                              </button>

                              <button
                                onClick={() => setSettingsAllocationRule('equal_split')}
                                className={`w-full text-left p-2 rounded-lg border transition-all cursor-pointer ${
                                  settingsAllocationRule === 'equal_split' ? 'border-purple-600 bg-purple-50/60' : 'border-gray-200 bg-white'
                                }`}
                              >
                                <div className="flex justify-between items-center mb-0.5">
                                  <span className="font-bold text-gray-900">3. Equal Split (Simple Jar)</span>
                                  {settingsAllocationRule === 'equal_split' && <Check size={10} className="text-purple-700" />}
                                </div>
                                <p className="text-[7px] text-gray-500 leading-tight">
                                  Equal distribution among all team members logged onto the shift regardless of hours.
                                </p>
                              </button>
                            </div>
                          </div>
                        )}

                        {/* ======================================================= */}
                        {/* 1.5 BUSINESS PROFILE                                     */}
                        {/* ======================================================= */}
                        {settingsActiveSection === 'business_profile' && (
                          <div className="space-y-2.5 animate-fade-in text-[8px]">
                            <span className="font-extrabold text-gray-900 text-[10px] block">Business Entity & Location Profile</span>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="bg-gray-50 p-2 rounded-lg border border-gray-150">
                                <span className="text-[7px] text-gray-400 uppercase font-bold block">Legal Entity Name</span>
                                <span className="font-bold text-gray-900">GrowEzy Hospitality LLC</span>
                              </div>
                              <div className="bg-gray-50 p-2 rounded-lg border border-gray-150">
                                <span className="text-[7px] text-gray-400 uppercase font-bold block">DBA / Branch</span>
                                <span className="font-bold text-gray-900">The Bistro Downtown</span>
                              </div>
                              <div className="bg-gray-50 p-2 rounded-lg border border-gray-150">
                                <span className="text-[7px] text-gray-400 uppercase font-bold block">Federal EIN / Tax ID</span>
                                <span className="font-mono font-bold text-gray-900">XX-XXX4819</span>
                              </div>
                              <div className="bg-gray-50 p-2 rounded-lg border border-gray-150">
                                <span className="text-[7px] text-gray-400 uppercase font-bold block">State Jurisdiction</span>
                                <span className="font-bold text-emerald-700">California (CA)</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ======================================================= */}
                        {/* 2.1 SUBSCRIPTION & BILLING                               */}
                        {/* ======================================================= */}
                        {settingsActiveSection === 'subscription_billing' && (
                          <div className="space-y-2.5 animate-fade-in text-[8px]">
                            <div className="flex justify-between items-center">
                              <span className="font-extrabold text-gray-900 text-[10px]">Subscription & Billing</span>
                              <span className="bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded text-[7px]">Beta Pro Active</span>
                            </div>
                            <div className="bg-purple-50/80 border border-purple-200 p-2.5 rounded-xl space-y-1.5">
                              <div className="flex justify-between items-center">
                                <div>
                                  <span className="font-bold text-purple-950 block text-[9px]">Growth Pro Tier</span>
                                  <span className="text-[7px] text-purple-700">Unlimited Staff • Automated POS Sync • E-Signatures</span>
                                </div>
                                <span className="font-black text-purple-900 text-[11px]">$49<span className="text-[7px] font-normal text-purple-600">/mo</span></span>
                              </div>
                              <div className="text-[7px] text-purple-800 bg-white/80 p-1.5 rounded-md border border-purple-200/60">
                                ✓ Beta Trial: 30 days remaining (Renews Aug 24, 2026)
                              </div>
                            </div>
                            <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg border border-gray-150">
                              <div className="flex items-center gap-1.5">
                                <CreditCard size={12} className="text-gray-500" />
                                <span className="font-mono text-gray-700">Visa ending in •••• 4242</span>
                              </div>
                              <span className="text-[7px] text-blue-600 font-bold">Manage Payment</span>
                            </div>
                          </div>
                        )}

                        {/* ======================================================= */}
                        {/* 2.2 REFERRAL PROGRAM                                     */}
                        {/* ======================================================= */}
                        {settingsActiveSection === 'referral_program' && (
                          <div className="space-y-2.5 animate-fade-in text-[8px]">
                            <span className="font-extrabold text-gray-900 text-[10px] block">Referral Program & Growth Commission</span>
                            <div className="bg-emerald-50 border border-emerald-200 p-2.5 rounded-xl space-y-1.5 text-emerald-950">
                              <div className="flex justify-between items-center">
                                <span className="font-bold text-[9px]">Earn 20% Recurring Commission</span>
                                <span className="bg-emerald-200 text-emerald-900 font-bold px-1.5 py-0.5 rounded text-[7px]">VIP Affiliate</span>
                              </div>
                              <p className="text-[7px] text-emerald-800 leading-tight">
                                Invite partner restaurants or bars to TipValue AI and receive 20% lifetime rev-share.
                              </p>
                              <div className="flex items-center justify-between bg-white p-1.5 rounded-lg border border-emerald-200">
                                <span className="font-mono font-bold text-[8px]">TIPVALUE-VIP2026</span>
                                <button className="bg-emerald-600 text-white font-bold px-2 py-0.5 rounded text-[7px]">Copy Code</button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ======================================================= */}
                        {/* 3.1 TIPFLOW GUIDE (5 SUB-TABS)                           */}
                        {/* ======================================================= */}
                        {settingsActiveSection === 'tipflow_guide' && (
                          <div className="space-y-2 animate-fade-in text-[8px]">
                            <div className="flex justify-between items-center">
                              <span className="font-extrabold text-gray-900 text-[10px]">TipFlow Documentation Hub</span>
                              <span className="text-[7px] text-gray-400">5 Guide Tabs</span>
                            </div>
                            
                            {/* 5 Guide Sub-Tabs */}
                            <div className="flex border-b border-gray-200 overflow-x-auto text-[7px] font-bold">
                              {[
                                { id: 1, label: '1. Overview' },
                                { id: 2, label: '2. Shifts' },
                                { id: 3, label: '3. Math' },
                                { id: 4, label: '4. IRS Form' },
                                { id: 5, label: '5. Payroll' }
                              ].map(tab => (
                                <button
                                  key={tab.id}
                                  onClick={() => setSettingsGuideSubTab(tab.id)}
                                  className={`px-1.5 py-1 transition-all cursor-pointer whitespace-nowrap ${
                                    settingsGuideSubTab === tab.id ? 'text-purple-700 border-b-2 border-purple-600 font-black' : 'text-gray-400 hover:text-gray-600'
                                  }`}
                                >
                                  {tab.label}
                                </button>
                              ))}
                            </div>

                            <div className="p-2 bg-gray-50 border border-gray-150 rounded-lg text-[7px] text-gray-700 leading-relaxed">
                              {settingsGuideSubTab === 1 && (
                                <p><strong>System Overview:</strong> TipValue automates the complete lifecycle from physical shift cash drops to electronic POS import and final payroll distribution.</p>
                              )}
                              {settingsGuideSubTab === 2 && (
                                <p><strong>Shift Operations:</strong> Staff log into the dashboard or POS terminals to record shifts, verifying their clock-in/out stamps daily.</p>
                              )}
                              {settingsGuideSubTab === 3 && (
                                <p><strong>Allocation Engine:</strong> Mathematical pool models dynamically adjust based on role weighting points and hours worked.</p>
                              )}
                              {settingsGuideSubTab === 4 && (
                                <p><strong>IRS Form 8027:</strong> Automated tracking of the 8% gross receipts allocation requirement to prevent year-end tax surprises.</p>
                              )}
                              {settingsGuideSubTab === 5 && (
                                <p><strong>Payroll Export:</strong> Pre-mapped format adapters for Gusto, ADP, Paychex, and QuickBooks Payroll.</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* ======================================================= */}
                        {/* 3.2 CONTACT SUPPORT                                      */}
                        {/* ======================================================= */}
                        {settingsActiveSection === 'contact_support' && (
                          <div className="space-y-2.5 animate-fade-in text-[8px]">
                            <div className="flex justify-between items-center">
                              <span className="font-extrabold text-gray-900 text-[10px]">Direct Support & Assistance</span>
                              <span className="bg-blue-100 text-blue-800 font-bold px-1.5 py-0.5 rounded text-[7px]">SLA: &lt;15 min</span>
                            </div>
                            <div className="bg-gray-50 border border-gray-150 p-2.5 rounded-xl space-y-1.5">
                              <span className="font-bold text-gray-800 block">Submit Support Ticket</span>
                              <input type="text" placeholder="Issue subject..." readOnly value="Assistance with Toast POS shift webhook sync" className="bg-white border border-gray-250 w-full p-1.5 rounded-md text-[8px]" />
                              <button className="bg-blue-600 text-white font-bold w-full py-1.5 rounded-lg text-[8px] cursor-pointer">
                                Open Priority Ticket
                              </button>
                            </div>
                          </div>
                        )}

                      </div>

                    </div>

                  </div>
                )}

                {/* 12. COMPLETED */}
                {currentStepData.mockView === 'completed' && (
                  <div className="flex flex-col items-center justify-center text-center h-full max-w-sm mx-auto space-y-4">
                    <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 shadow-lg shadow-emerald-500/10">
                      <CheckCircle size={32} className="animate-bounce" />
                    </div>
                    <div className="space-y-1">
                      <h5 className="font-extrabold text-gray-900 text-[14px]">You're Ready to Use TipValue AI!</h5>
                      <p className="text-[9px] text-gray-500 leading-normal">
                        Your workspace is equipped for daily employee submissions, automated POS importing, digital e-signatures, dispute reconciliation, deep settings configuration, and rapid payroll exports.
                      </p>
                    </div>
                    
                    <div className="flex gap-2 w-full pt-1">
                      <button 
                        onClick={() => setActiveStep(0)}
                        className="flex-1 bg-white border border-gray-250 hover:bg-gray-100 text-gray-700 font-extrabold py-2 rounded-xl text-[9px] transition-all cursor-pointer flex items-center justify-center gap-1"
                      >
                        <RotateCcw size={12} />
                        <span>Restart Tour</span>
                      </button>
                      <button 
                        onClick={onClose}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-extrabold py-2 rounded-xl text-[9px] transition-all cursor-pointer shadow shadow-blue-500/20"
                      >
                        Exit Walkthrough
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>

          </div>

          {/* SIMULATED FLOATING CURSOR POINTER (AUTOMATED VIDEO MODE ONLY) */}
          {mode === 'autoplay' && (
            <motion.div
              style={{
                position: 'absolute',
                pointerEvents: 'none',
                zIndex: 9999
              }}
              animate={{
                left: cursorPos.x,
                top: cursorPos.y,
                opacity: cursorPos.opacity
              }}
              transition={{
                type: 'spring',
                damping: 24,
                stiffness: 85
              }}
              className="absolute select-none -translate-x-1/2 -translate-y-1/2"
            >
              {cursorPos.clickEffect && (
                <div className="absolute w-8 h-8 rounded-full border border-blue-500 bg-blue-500/30 animate-ping"></div>
              )}
              <MousePointer size={18} className="text-blue-600 fill-blue-600 drop-shadow-md transform -rotate-12 stroke-white stroke-2" />
              <div className="absolute top-5 left-2 bg-blue-600 text-white text-[7px] font-black uppercase tracking-wider px-1 rounded shadow-sm border border-white/20 animate-pulse whitespace-nowrap">
                Clicking...
              </div>
            </motion.div>
          )}

        </section>

      </main>

      {/* 4. FOOTER STATUS BAR */}
      <footer className="px-6 py-3.5 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center text-xs text-gray-500 bg-gray-900/20">
        <div className="flex items-center gap-3">
          <span className="font-extrabold uppercase tracking-widest text-[9px] text-blue-500">System Checklist</span>
          <span className="text-gray-400">Timezone: UTC</span>
          <span>Tenant Domain: tipvalue.com</span>
        </div>
        <div className="mt-2 sm:mt-0 text-[10px] text-gray-400">
          TipValue is authorized by © 2026 GrowEzy Platform Systems
        </div>
      </footer>

    </div>
  );
};
